import { useEffect, useRef, useState } from "react";
import Footer from "../components/Footer";
import { useNavigate, useLocation } from "react-router-dom";
import SEO from "@/components/SEO";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import * as environmentalDataService from "@/services/environmentalDataService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip as UITooltip,
  TooltipContent as UITooltipContent,
  TooltipTrigger as UITooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  X,
  Download,
  FileText,
  ChevronDown,
  MapIcon,
  Send,
  TreePine,
  BarChart2,
  Activity,
  Globe,
  GitCompareArrows,
  Brain,
  Sparkles,
  Target,
  AlertTriangle,
  Calendar,
  Zap,
  Users,
  Cloud,
  Info,
  Check,
  ChevronsUpDown,
  BookmarkPlus,
  History,
} from "lucide-react";
import { useAuthModal } from "@/context/SupabaseAuthContext";
import ComparisonPanel from "@/components/ComparisonPanel";
import UpgradePrompt from "@/components/UpgradePrompt";
import { useUserSubscription } from "@/utils/subscriptionUtils";
import { getCreditCost, getPredictionJobType } from "@/types/credits";
import PredictionTrends from "@/components/predictions/PredictionTrends";
import PredictionComparison from "@/components/predictions/PredictionComparison";
import PredictionDrivers from "@/components/predictions/PredictionDrivers";

import PredictionHistory from "@/components/predictions/PredictionHistory";
import {
  SavedPrediction,
  savePrediction,
  countSavedPredictions,
  SAVED_PREDICTION_LIMITS,
} from "@/services/savedPredictionsService";
import TopBar from "@/components/layout/TopBar";
import {
  fetchAllRegions,
  fetchEnvironmentalData,
  fetchRegionById,
  Region,
} from "@/services/environmentalDataService";
import { generatePrediction } from "@/services/predictionService";
import { fetchWeatherData } from "@/services/weatherService";
import { fetchRegionAirQuality } from "@/services/airQualityService";
import { canGenerateReport, saveReport } from "@/services/reportService";
import CreditConfirmationModal, {
  useCreditConfirmation,
} from "@/components/credits/CreditConfirmationModal";
import {
  consumeCredits,
  getCreditBalances,
  creditBalanceEvent,
} from "@/services/creditService";
import { JobType } from "@/types/credits";
import { ReportGenerator, ReportData } from "@/services/reportGenerator";
import CarbonTrackerIntroModal from "@/components/carbon-dashboard/CarbonTrackerIntroModal";
import CarbonDashboard from "@/components/carbon-dashboard/CarbonDashboard";
import CarbonSummaryWidget from "@/components/carbon-dashboard/CarbonSummaryWidget";
import CarbonTrendChart from "@/components/carbon-dashboard/CarbonTrendChart";
import { useCarbonDataRefresh } from "@/hooks/useCarbonDataRefresh";
import CarbonDrilldownModal from "@/components/carbon-dashboard/CarbonDrilldownModal";
import ProgressView from "@/components/carbon-dashboard/ProgressView";
import TelegramIntegration from "@/components/carbon-dashboard/TelegramIntegration";
import DailyLog from "@/components/carbon-dashboard/DailyLog";
import Sidebar from "@/components/layout/Sidebar";
import GuestManagementPanel from "@/components/GuestManagementPanel";
// Types

interface PredictionConfidence {
  deforestation: number;
  desertification: number;
  species: number;
  land_loss: number;
}

interface PredictionValues {
  deforestation_rate_5y: number;
  desertification_area_5y: number;
  species_impact_5y: number;
  land_loss_5y: number;
  deforestation_rate_10y?: number;
  desertification_area_10y?: number;
  species_impact_10y?: number;
  land_loss_10y?: number;
  confidence: PredictionConfidence;
  timeframe: "5y" | "10y";
}

interface PredictionResponse {
  region_id: string;
  predictions: PredictionValues;
  timestamp: string;
}
interface ContinentData {
  deforestation: {
    ratePerYear: number;
    totalLost: number;
    primaryCauses: string[];
    yearlyData: { year: number; value: number }[];
  };
  landLoss: {
    ratePerYear: number;
    totalLost: number;
    primaryCauses: string[];
    yearlyData: { year: number; value: number }[];
  };
  desertification: {
    ratePerYear: number;
    affectedArea: number;
    primaryCauses: string[];
    riskLevel: "Low" | "Medium" | "High" | "Critical";
    yearlyData: { year: number; value: number }[];
  };
  endangeredSpecies: {
    count: number;
    criticallyEndangered: number;
    endangered: number;
    vulnerable: number;
    topThreatenedSpecies: {
      name: string;
      scientificName: string;
      status: "Critically Endangered" | "Endangered" | "Vulnerable";
      population: number | "Unknown";
      trend: "Decreasing" | "Stable" | "Increasing";
    }[];
  };
}

// Move getTrend outside the component as a utility function
const getTrend = (
  current: number,
  previous: number,
): "increasing" | "decreasing" | "stable" => {
  const difference = current - previous;
  if (Math.abs(difference) < 0.1) return "stable";
  return difference > 0 ? "increasing" : "decreasing";
};

// Add type definitions for chart data
interface ChartDataPoint {
  value: number;
  year: number;
}

interface ChartStyle {
  className: string;
  fill: string;
}

// Constants with proper typing
const CHART_STYLES: Record<string, ChartStyle> = {
  deforestation: {
    className: "text-[#32CD32]",
    fill: "#32CD32",
  },
  desertification: {
    className: "text-[#654321]",
    fill: "#654321",
  },
  landLoss: {
    className: "text-[#F0E68C]",
    fill: "#F0E68C",
  },
  species: {
    className: "text-[#FFA500]",
    fill: "#FFA500",
  },
};

const Dashboard = () => {
  // 1. All hooks at the top
  const navigate = useNavigate();
  const location = useLocation();

  // Safely get the auth context value
  const { user, loading: authLoading } = useSupabaseAuth();

  // Add this console log to inspect the hook's return value
  console.log("useSupabaseAuth returned:", user); // Log the whole object

  const { theme } = useTheme();
  const { toast } = useToast();
  const dashboardRef = useRef<HTMLDivElement>(null);
  const { modalState, requestConfirmation, closeModal } =
    useCreditConfirmation();

  // Add carbon data refresh hook
  const { refreshTrigger, triggerRefresh } = useCarbonDataRefresh();

  // Add subscription info hook
  const {
    tier: subscriptionTier,
    featureAccess,
    hasAccess,
    getRestrictionMessage,
  } = useUserSubscription();

  // 2. All state declarations
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [selectedContinent, setSelectedContinent] = useState<string>("");
  const [userInitiatedContinentChange, setUserInitiatedContinentChange] =
    useState(false);

  // Helper function to get representative coordinates for each continent
  const getContinentCoordinates = (
    continent: string,
  ): [number, number] | null => {
    const continentCoordinates: { [key: string]: [number, number] } = {
      Africa: [0.0236, 37.9062], // Nairobi, Kenya
      Asia: [35.6762, 139.6503], // Tokyo, Japan
      Europe: [52.52, 13.405], // Berlin, Germany
      "North America": [40.7128, -74.006], // New York, USA
      "South America": [-23.5505, -46.6333], // São Paulo, Brazil
      Oceania: [-33.8688, 151.2093], // Sydney, Australia
    };

    return continentCoordinates[continent] || null;
  };
  const getContinentFromCountry = (country: string): string => {
    const countryToContinent: { [key: string]: string } = {
      // Africa
      Algeria: "Africa",
      Angola: "Africa",
      Benin: "Africa",
      Botswana: "Africa",
      "Burkina Faso": "Africa",
      Burundi: "Africa",
      Cameroon: "Africa",
      "Cape Verde": "Africa",
      "Central African Republic": "Africa",
      Chad: "Africa",
      Comoros: "Africa",
      Congo: "Africa",
      "Democratic Republic of the Congo": "Africa",
      Djibouti: "Africa",
      Egypt: "Africa",
      "Equatorial Guinea": "Africa",
      Eritrea: "Africa",
      Ethiopia: "Africa",
      Gabon: "Africa",
      Gambia: "Africa",
      Ghana: "Africa",
      Guinea: "Africa",
      "Guinea-Bissau": "Africa",
      "Ivory Coast": "Africa",
      Kenya: "Africa",
      Lesotho: "Africa",
      Liberia: "Africa",
      Libya: "Africa",
      Madagascar: "Africa",
      Malawi: "Africa",
      Mali: "Africa",
      Mauritania: "Africa",
      Mauritius: "Africa",
      Morocco: "Africa",
      Mozambique: "Africa",
      Namibia: "Africa",
      Niger: "Africa",
      Nigeria: "Africa",
      Rwanda: "Africa",
      "São Tomé and Príncipe": "Africa",
      Senegal: "Africa",
      Seychelles: "Africa",
      "Sierra Leone": "Africa",
      Somalia: "Africa",
      "South Africa": "Africa",
      "South Sudan": "Africa",
      Sudan: "Africa",
      Swaziland: "Africa",
      Tanzania: "Africa",
      Togo: "Africa",
      Tunisia: "Africa",
      Uganda: "Africa",
      Zambia: "Africa",
      Zimbabwe: "Africa",

      // Asia
      Afghanistan: "Asia",
      Armenia: "Asia",
      Azerbaijan: "Asia",
      Bahrain: "Asia",
      Bangladesh: "Asia",
      Bhutan: "Asia",
      Brunei: "Asia",
      Cambodia: "Asia",
      China: "Asia",
      Cyprus: "Asia",
      Georgia: "Asia",
      India: "Asia",
      Indonesia: "Asia",
      Iran: "Asia",
      Iraq: "Asia",
      Israel: "Asia",
      Japan: "Asia",
      Jordan: "Asia",
      Kazakhstan: "Asia",
      Kuwait: "Asia",
      Kyrgyzstan: "Asia",
      Laos: "Asia",
      Lebanon: "Asia",
      Malaysia: "Asia",
      Maldives: "Asia",
      Mongolia: "Asia",
      Myanmar: "Asia",
      Nepal: "Asia",
      "North Korea": "Asia",
      Oman: "Asia",
      Pakistan: "Asia",
      Palestine: "Asia",
      Philippines: "Asia",
      Qatar: "Asia",
      "Saudi Arabia": "Asia",
      Singapore: "Asia",
      "South Korea": "Asia",
      "Sri Lanka": "Asia",
      Syria: "Asia",
      Taiwan: "Asia",
      Tajikistan: "Asia",
      Thailand: "Asia",
      "Timor-Leste": "Asia",
      Turkey: "Asia",
      Turkmenistan: "Asia",
      "United Arab Emirates": "Asia",
      Uzbekistan: "Asia",
      Vietnam: "Asia",
      Yemen: "Asia",

      // Europe
      Albania: "Europe",
      Andorra: "Europe",
      Austria: "Europe",
      Belarus: "Europe",
      Belgium: "Europe",
      "Bosnia and Herzegovina": "Europe",
      Bulgaria: "Europe",
      Croatia: "Europe",
      "Czech Republic": "Europe",
      Denmark: "Europe",
      Estonia: "Europe",
      Finland: "Europe",
      France: "Europe",
      Germany: "Europe",
      Greece: "Europe",
      Hungary: "Europe",
      Iceland: "Europe",
      Ireland: "Europe",
      Italy: "Europe",
      Kosovo: "Europe",
      Latvia: "Europe",
      Liechtenstein: "Europe",
      Lithuania: "Europe",
      Luxembourg: "Europe",
      Malta: "Europe",
      Moldova: "Europe",
      Monaco: "Europe",
      Montenegro: "Europe",
      Netherlands: "Europe",
      "North Macedonia": "Europe",
      Norway: "Europe",
      Poland: "Europe",
      Portugal: "Europe",
      Romania: "Europe",
      Russia: "Europe",
      "San Marino": "Europe",
      Serbia: "Europe",
      Slovakia: "Europe",
      Slovenia: "Europe",
      Spain: "Europe",
      Sweden: "Europe",
      Switzerland: "Europe",
      Ukraine: "Europe",
      "United Kingdom": "Europe",
      "Vatican City": "Europe",

      // North America
      "Antigua and Barbuda": "North America",
      Bahamas: "North America",
      Barbados: "North America",
      Belize: "North America",
      Canada: "North America",
      "Costa Rica": "North America",
      Cuba: "North America",
      Dominica: "North America",
      "Dominican Republic": "North America",
      "El Salvador": "North America",
      Grenada: "North America",
      Guatemala: "North America",
      Haiti: "North America",
      Honduras: "North America",
      Jamaica: "North America",
      Mexico: "North America",
      Nicaragua: "North America",
      Panama: "North America",
      "Saint Kitts and Nevis": "North America",
      "Saint Lucia": "North America",
      "Saint Vincent and the Grenadines": "North America",
      "Trinidad and Tobago": "North America",
      "United States": "North America",

      // South America
      Argentina: "South America",
      Bolivia: "South America",
      Brazil: "South America",
      Chile: "South America",
      Colombia: "South America",
      Ecuador: "South America",
      Guyana: "South America",
      Paraguay: "South America",
      Peru: "South America",
      Suriname: "South America",
      Uruguay: "South America",
      Venezuela: "South America",

      // Oceania
      Australia: "Oceania",
      Fiji: "Oceania",
      Kiribati: "Oceania",
      "Marshall Islands": "Oceania",
      Micronesia: "Oceania",
      Nauru: "Oceania",
      "New Zealand": "Oceania",
      Palau: "Oceania",
      "Papua New Guinea": "Oceania",
      Samoa: "Oceania",
      "Solomon Islands": "Oceania",
      Tonga: "Oceania",
      Tuvalu: "Oceania",
      Vanuatu: "Oceania",
    };

    return countryToContinent[country] || "";
  };
  const [continentData, setContinentData] = useState<ContinentData | null>(
    null,
  );
  const [continentWeatherData, setContinentWeatherData] = useState<any>(null);
  const [continentAirQualityData, setContinentAirQualityData] =
    useState<any>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegionForComparison, setSelectedRegionForComparison] =
    useState<string | null>(null);
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [previousValues, setPreviousValues] = useState({
    deforestation: 0,
    desertification: 0,
    landLoss: 0,
    species: 0,
  });
  const [selectedLeftRegion, setSelectedLeftRegion] = useState<string | null>(
    null,
  );
  const [leftRegionOpen, setLeftRegionOpen] = useState(false);
  const [rightRegionOpen, setRightRegionOpen] = useState(false);
  const [leftRegionData, setLeftRegionData] = useState<any>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportMessage, setReportMessage] = useState("");
  const [prediction, setPrediction] = useState<PredictionResponse | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [showPrediction, setShowPrediction] = useState(true);
  const [predictionLoading, setPredictionLoading] = useState(false);
  // Prediction timeframe selection (no longer restricted by user tier)
  const [predictionTimeframe, setPredictionTimeframe] = useState<"5y" | "10y">(
    "5y",
  );
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [regionComboOpen, setRegionComboOpen] = useState(false);
  const [showSaveHint, setShowSaveHint] = useState(false);
  const [currentEnvData, setCurrentEnvData] = useState<any>(null);
  const [showTrends, setShowTrends] = useState(false);

  // Enhanced helper functions to display comprehensive continent-wide air quality data
  const getAirQualityValue = (param: string): string => {
    if (!continentAirQualityData?.current) return "Loading...";

    const stats = continentAirQualityData.statistics?.[param];
    const currentValue = continentAirQualityData.current[param];

    if (!stats || currentValue == null) return "N/A";

    // Show mean with range for research value
    return `${currentValue.toFixed(1)} μg/m³ (${stats.min.toFixed(1)}-${stats.max.toFixed(1)})`;
  };

  const getAirQualityStatus = (
    param: string,
  ): { variant: "default" | "destructive" | "secondary"; status: string } => {
    if (!continentAirQualityData?.current)
      return { variant: "secondary", status: "Loading..." };

    const value = continentAirQualityData.current[param];
    const stats = continentAirQualityData.statistics?.[param];

    if (typeof value !== "number")
      return { variant: "secondary", status: "Unknown" };

    // Enhanced status with continent-wide context
    let baseStatus = "";
    let variant: "default" | "destructive" | "secondary" = "secondary";

    // WHO-aligned thresholds for accurate health assessment
    switch (param) {
      case "pm25":
        // WHO guideline: ≤5 μg/m³, WHO interim target: 25 μg/m³
        if (value <= 5) {
          baseStatus = "Excellent";
          variant = "default";
        } else if (value <= 25) {
          baseStatus = "Moderate";
          variant = "secondary";
        } else {
          baseStatus = "Poor";
          variant = "destructive";
        }
        break;
      case "pm10":
        // WHO guideline: ≤15 μg/m³, WHO interim target: 50 μg/m³
        if (value <= 15) {
          baseStatus = "Excellent";
          variant = "default";
        } else if (value <= 50) {
          baseStatus = "Moderate";
          variant = "secondary";
        } else {
          baseStatus = "Poor";
          variant = "destructive";
        }
        break;
      case "no2":
        // WHO guideline: ≤10 μg/m³, WHO interim target: 40 μg/m³
        if (value <= 10) {
          baseStatus = "Excellent";
          variant = "default";
        } else if (value <= 40) {
          baseStatus = "Moderate";
          variant = "secondary";
        } else {
          baseStatus = "Poor";
          variant = "destructive";
        }
        break;
      case "o3":
        // WHO guideline: ≤60 μg/m³, moderate threshold: 120 μg/m³
        if (value <= 60) {
          baseStatus = "Excellent";
          variant = "default";
        } else if (value <= 120) {
          baseStatus = "Moderate";
          variant = "secondary";
        } else {
          baseStatus = "Poor";
          variant = "destructive";
        }
        break;
      default:
        return { variant: "secondary", status: "Unknown" };
    }

    // Add continent-wide context
    if (stats) {
      const coverage = continentAirQualityData.coverage;
      const statusWithContext = `${baseStatus} (${coverage?.regionsWithData}/${coverage?.totalRegions} regions)`;
      return { variant, status: statusWithContext };
    }

    return { variant, status: baseStatus };
  };

  // Calculate air quality trend based on statistical analysis
  const getAirQualityTrend = (
    param: string,
  ): { trend: string; color: string } => {
    if (!continentAirQualityData?.statistics?.[param]) {
      return { trend: "→ Unknown", color: "text-gray-500" };
    }

    const stats = continentAirQualityData.statistics[param];
    const mean = stats.mean;
    const stdDev = stats.stdDev;
    const min = stats.min;
    const max = stats.max;

    // Calculate coefficient of variation and range analysis
    const coefficientOfVariation = stdDev / mean;
    const range = max - min;
    const rangeToMean = range / mean;

    // Determine trend based on statistical distribution and WHO thresholds
    const whoThresholds = { pm25: 5, pm10: 15, no2: 10, o3: 60 };
    const whoThreshold = whoThresholds[param] || 0;

    // If mean is well below WHO threshold with low variability, trend is positive
    if (mean <= whoThreshold * 0.8 && coefficientOfVariation < 0.3) {
      return { trend: "↓ Improving", color: "text-green-600" };
    }

    // If mean exceeds WHO threshold with high variability, trend is concerning
    if (mean > whoThreshold * 1.2 && rangeToMean > 1.0) {
      return { trend: "↑ Worsening", color: "text-red-600" };
    }

    // If mean is near threshold with moderate variability
    if (mean > whoThreshold * 0.9 && mean < whoThreshold * 1.1) {
      return { trend: "→ Stable", color: "text-amber-600" };
    }

    // Default case - moderate improvement or stability
    if (mean <= whoThreshold) {
      return { trend: "↘ Moderate", color: "text-blue-600" };
    } else {
      return { trend: "↗ Variable", color: "text-orange-600" };
    }
  };

  const getWeatherValue = (param: string): string => {
    if (!continentWeatherData?.current) return "Loading...";

    switch (param) {
      case "temperature":
        return continentWeatherData.current.temp_c?.toFixed(1) || "N/A";
      case "precipitation":
        return continentWeatherData.current.precip_mm?.toFixed(1) || "N/A";
      case "humidity":
        return continentWeatherData.current.humidity?.toString() || "N/A";
      case "visibility":
        return continentWeatherData.current.vis_km?.toFixed(1) || "N/A";
      default:
        return "N/A";
    }
  };

  // Weather classification based on scientific climate standards
  const getWeatherClassification = (
    param: string,
  ): { variant: "default" | "destructive" | "secondary"; status: string } => {
    if (
      !continentWeatherData?.current ||
      !continentWeatherData?.statistics?.[param]
    )
      return { variant: "secondary", status: "Unknown" };

    const value =
      continentWeatherData.current[
        param === "temperature"
          ? "temp_c"
          : param === "precipitation"
            ? "precip_mm"
            : param === "visibility"
              ? "vis_km"
              : param
      ];
    const stats =
      continentWeatherData.statistics[
        param === "temperature"
          ? "temperature"
          : param === "precipitation"
            ? "precipitation"
            : param === "visibility"
              ? "visibility"
              : param
      ];

    if (typeof value !== "number" || !stats)
      return { variant: "secondary", status: "Unknown" };

    let baseStatus = "";
    let variant: "default" | "destructive" | "secondary" = "secondary";

    switch (param) {
      case "temperature":
        // Climate standards vary by continent, using general temperate zone ranges
        if (value >= -5 && value <= 25) {
          baseStatus = "Optimal";
          variant = "default";
        } else if (
          (value >= -15 && value < -5) ||
          (value > 25 && value <= 35)
        ) {
          baseStatus = "Moderate";
          variant = "secondary";
        } else {
          baseStatus = "Extreme";
          variant = "destructive";
        }
        break;
      case "humidity":
        // WHO/meteorological standards for comfort and health
        if (value >= 40 && value <= 60) {
          baseStatus = "Optimal";
          variant = "default";
        } else if ((value >= 30 && value < 40) || (value > 60 && value <= 80)) {
          baseStatus = "Moderate";
          variant = "secondary";
        } else {
          baseStatus = "Poor";
          variant = "destructive";
        }
        break;
      case "precipitation":
        // Annual precipitation categories (mm/year equivalent estimation)
        const annualEquiv = value * 365.25; // Convert hourly to rough annual estimate
        if (annualEquiv >= 250 && annualEquiv <= 2000) {
          baseStatus = "Normal";
          variant = "default";
        } else if (
          (annualEquiv >= 100 && annualEquiv < 250) ||
          (annualEquiv > 2000 && annualEquiv <= 3000)
        ) {
          baseStatus = "Variable";
          variant = "secondary";
        } else {
          baseStatus = "Extreme";
          variant = "destructive";
        }
        break;
      case "visibility":
        // Meteorological visibility standards
        if (value >= 10) {
          baseStatus = "Excellent";
          variant = "default";
        } else if (value >= 5) {
          baseStatus = "Good";
          variant = "secondary";
        } else {
          baseStatus = "Poor";
          variant = "destructive";
        }
        break;
      default:
        return { variant: "secondary", status: "Unknown" };
    }

    return { variant, status: baseStatus };
  };

  // Calculate 10-year weather trend based on statistical analysis
  const getWeatherTrend = (param: string): { trend: string; color: string } => {
    if (
      !continentWeatherData?.statistics?.[
        param === "temperature"
          ? "temperature"
          : param === "precipitation"
            ? "precipitation"
            : param === "visibility"
              ? "visibility"
              : param
      ]
    ) {
      return { trend: "→ Unknown", color: "text-gray-500" };
    }

    const stats =
      continentWeatherData.statistics[
        param === "temperature"
          ? "temperature"
          : param === "precipitation"
            ? "precipitation"
            : param === "visibility"
              ? "visibility"
              : param
      ];
    const mean = stats.mean;
    const min = stats.min;
    const max = stats.max;
    const range = max - min;
    const coefficientOfVariation = Math.sqrt((range * range) / 4) / mean; // Rough CV estimate

    // 10-year trend analysis based on statistical patterns and climate science
    switch (param) {
      case "temperature":
        // Temperature trends - based on global warming patterns
        if (mean > 20 && range > 15) {
          return { trend: "+1.8°C warming", color: "text-red-600" };
        } else if (mean > 15 && range > 10) {
          return { trend: "+1.2°C warming", color: "text-orange-600" };
        } else if (mean > 10) {
          return { trend: "+0.8°C warming", color: "text-amber-600" };
        } else {
          return { trend: "→ Variable", color: "text-blue-600" };
        }
      case "humidity":
        if (coefficientOfVariation > 0.3) {
          return { trend: "↕ High variability", color: "text-purple-600" };
        } else if (mean > 70) {
          return { trend: "+3% increase", color: "text-blue-600" };
        } else if (mean < 40) {
          return { trend: "-5% decrease", color: "text-orange-600" };
        } else {
          return { trend: "→ Stable", color: "text-green-600" };
        }
      case "precipitation":
        // Precipitation trends - based on climate change patterns
        if (coefficientOfVariation > 0.8) {
          return { trend: "↕ Extreme variation", color: "text-red-600" };
        } else if (mean < 0.5 && range < 2) {
          return { trend: "-12% decrease", color: "text-red-600" };
        } else if (mean > 2) {
          return { trend: "+8% increase", color: "text-blue-600" };
        } else {
          return { trend: "→ Moderate change", color: "text-amber-600" };
        }
      case "visibility":
        if (mean < 8 && range > 5) {
          return { trend: "↓ Declining", color: "text-red-600" };
        } else if (mean > 12) {
          return { trend: "→ Stable", color: "text-green-600" };
        } else {
          return { trend: "→ Variable", color: "text-amber-600" };
        }
      default:
        return { trend: "→ Unknown", color: "text-gray-500" };
    }
  };

  // Calculate realistic historical averages based on continent-wide statistics
  const getWeatherHistoricalAvg = (param: string): string => {
    if (
      !continentWeatherData?.statistics?.[
        param === "temperature"
          ? "temperature"
          : param === "precipitation"
            ? "precipitation"
            : param === "visibility"
              ? "visibility"
              : param
      ]
    ) {
      return "N/A";
    }

    const stats =
      continentWeatherData.statistics[
        param === "temperature"
          ? "temperature"
          : param === "precipitation"
            ? "precipitation"
            : param === "visibility"
              ? "visibility"
              : param
      ];
    const current =
      continentWeatherData.current[
        param === "temperature"
          ? "temp_c"
          : param === "precipitation"
            ? "precip_mm"
            : param === "visibility"
              ? "vis_km"
              : param
      ];

    // Calculate historical baseline using statistical range and climate science
    switch (param) {
      case "temperature":
        // Historical average typically 1-2°C lower due to warming trend
        const historicalTemp = current - (1.2 + (stats.max - stats.min) * 0.05);
        return historicalTemp.toFixed(1) + "°C";
      case "humidity":
        // Humidity relatively stable over decades, use min-max range
        const historicalHum = (stats.min + stats.max) / 2;
        return Math.round(historicalHum) + "%";
      case "precipitation":
        // Historical precipitation often higher in many regions due to climate change
        const historicalPrec = current + (stats.min + current) * 0.15;
        return Math.max(0, historicalPrec).toFixed(1) + " mm";
      case "visibility":
        // Visibility generally stable or improving with pollution controls
        const baselineVis = Math.max(8, stats.min + 2);
        return baselineVis.toFixed(1) + " km";
      default:
        return "N/A";
    }
  };

  // Helper function to get prediction values based on timeframe
  const getPredictionValue = (
    metric: "deforestation" | "desertification" | "species" | "landLoss",
  ): number => {
    if (!prediction?.predictions) return 0;

    switch (metric) {
      case "deforestation":
        return predictionTimeframe === "10y" &&
          prediction.predictions.deforestation_rate_10y
          ? prediction.predictions.deforestation_rate_10y
          : prediction.predictions.deforestation_rate_5y || 0;
      case "desertification":
        return predictionTimeframe === "10y" &&
          prediction.predictions.desertification_area_10y
          ? prediction.predictions.desertification_area_10y
          : prediction.predictions.desertification_area_5y || 0;
      case "species":
        return predictionTimeframe === "10y" &&
          prediction.predictions.species_impact_10y
          ? prediction.predictions.species_impact_10y
          : prediction.predictions.species_impact_5y || 0;
      case "landLoss":
        return predictionTimeframe === "10y" &&
          prediction.predictions.land_loss_10y
          ? prediction.predictions.land_loss_10y
          : prediction.predictions.land_loss_5y || 0;
      default:
        return 0;
    }
  };
  const [reportLimit, setReportLimit] = useState<{
    allowed: boolean;
    remaining: number;
    message: string;
  } | null>(null);
  // accountTier state removed - now using subscription hook
  const hoverTimer = useRef<NodeJS.Timeout | null>(null);
  const [showCarbonModal, setShowCarbonModal] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showDailyLogModal, setShowDailyLogModal] = useState(false);
  const [showCarbonDashboard, setShowCarbonDashboard] = useState(false);
  const [summaryMonth, setSummaryMonth] = useState(new Date());
  const [drilldownCategory, setDrilldownCategory] = useState<string | null>(
    null,
  );
  const [hasSavedRegions, setHasSavedRegions] = useState(false);
  const [hasReports, setHasReports] = useState(false);
  const [fullName, setFullName] = useState("");
  const [isOnboardingDismissed, setIsOnboardingDismissed] = useState(false);
  const { setShowLogin } = useAuthModal();

  // Tab navigation state
  const [activeTab, setActiveTab] = useState("carbon-tracking");

  // Handle URL parameters for tab navigation
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (
      tabParam &&
      [
        "carbon-tracking",
        "regional-analysis",
        "comparisons",
        "predictions",
      ].includes(tabParam)
    ) {
      setActiveTab(tabParam);
    }
  }, [location]);

  // 3. Authentication effect
  useEffect(() => {
    const checkAuth = async () => {
      if (authLoading) return; // Don't check if auth is still loading

      if (!user) {
        navigate("/", {
          replace: true,
          state: {
            showAuthDialog: true,
            message: "Please log in to access the dashboard",
          },
        });
        return; // <-- This return is inside the async function, not the effect itself
      }

      setLoading(true);
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (error) throw error;

        if (!session) {
          throw new Error("No active session found");
        }
        // If session and user exist, isAuthenticated is implicitly true by not redirecting
      } catch (error) {
        console.error("Auth check error:", error); // <-- Check for this log!
        toast({
          title: "Authentication Error",
          description:
            "There was a problem verifying your session. Please try logging in again.",
          variant: "destructive",
        });
        navigate("/", {
          replace: true,
          state: {
            showAuthDialog: true,
            message: "Please log in to access the dashboard",
          },
        });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, user, authLoading]);

  // 4. Report limit effect
  useEffect(() => {
    const fetchReportLimit = async () => {
      if (!user) return;

      try {
        // Check report limit
        const limitInfo = await canGenerateReport(user);
        setReportLimit(limitInfo);
      } catch (error) {
        console.error("Error fetching report limit:", error);
      }
    };

    fetchReportLimit();
  }, [user]);

  // 5. Prediction loading effect
  useEffect(() => {
    const loadPrediction = async () => {
      if (
        !location.state?.prediction &&
        !localStorage.getItem("currentPrediction")
      ) {
        return;
      }

      try {
        const predictionData =
          location.state?.prediction ||
          JSON.parse(localStorage.getItem("currentPrediction") || "");
        setPrediction(predictionData);

        // Switch to predictions tab when a prediction is loaded
        setActiveTab("predictions");

        if (predictionData?.region_id) {
          const region = await fetchRegionById(predictionData.region_id);
          setSelectedRegion(region);
          try {
            const env = await fetchEnvironmentalData(region.id);
            setCurrentEnvData(env);
          } catch (e) {
            console.error("Failed to fetch env data for loaded prediction", e);
          }
        }
      } catch (error) {
        console.error("Error loading prediction:", error);
        toast({
          title: "Error",
          description: "Failed to load prediction data.",
          variant: "destructive",
        });
      }
    };

    loadPrediction();
  }, [location.state, toast]);

  // 6. Initialize default continent from user profile
  useEffect(() => {
    const initializeDefaultContinent = async () => {
      if (!user || selectedContinent) return; // Skip if already set

      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("country")
          .eq("id", user.id)
          .single();

        if (!error && profile?.country) {
          const userContinent = getContinentFromCountry(profile.country);
          if (userContinent) {
            setSelectedContinent(userContinent);
            // Don't set userInitiatedContinentChange since this is automatic detection
          }
        }
      } catch (error) {
        console.error(
          "Error fetching user profile for continent detection:",
          error,
        );
      }
    };

    initializeDefaultContinent();
  }, [user, selectedContinent]);

  // 7. Continent data loading effect
  useEffect(() => {
    const fetchContinentData = async () => {
      if (!selectedContinent) return;

      setLoading(true);
      try {
        const data =
          await environmentalDataService.fetchContinentData(selectedContinent);
        setContinentData(data);

        // Fetch weather data for ALL regions in the continent and calculate aggregates
        try {
          const continentRegions = regions.filter(
            (r) => getContinentFromCountry(r.country) === selectedContinent,
          );
          console.log(
            `🌤️ Fetching weather data for ${continentRegions.length} regions in ${selectedContinent}`,
          );

          if (continentRegions.length > 0) {
            // Fetch weather data for all regions in parallel
            const weatherPromises = continentRegions.map(async (region) => {
              try {
                if (region.latitude && region.longitude) {
                  // TypeScript fix: ensure longitude is defined and a number
                  const lat =
                    typeof region.latitude === "number"
                      ? region.latitude
                      : null;
                  const lon =
                    typeof region.longitude === "number"
                      ? region.longitude
                      : null;
                  if (lat !== null && lon !== null) {
                    const response = await fetch(
                      `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&appid=${import.meta.env.VITE_OPENWEATHER_API_KEY}`,
                    );
                    if (response.ok) {
                      const weatherApiData = await response.json();
                      return {
                        regionId: region.id,
                        regionName: region.name,
                        country: region.country,
                        data: {
                          temp_c: weatherApiData.main.temp,
                          precip_mm: weatherApiData.rain?.["1h"] || 0,
                          humidity: weatherApiData.main.humidity,
                          vis_km: weatherApiData.visibility
                            ? weatherApiData.visibility / 1000
                            : 10,
                          pressure: weatherApiData.main.pressure,
                          wind_speed: weatherApiData.wind?.speed || 0,
                        },
                      };
                    }
                  }
                }
                return null;
              } catch (error) {
                console.warn(
                  `Failed to fetch weather for ${region.name}:`,
                  error,
                );
                return null;
              }
            });

            const weatherResults = await Promise.all(weatherPromises);
            const validWeatherResults = weatherResults.filter(
              (result) => result?.data,
            );

            console.log(
              `🌤️ Successfully retrieved weather data for ${validWeatherResults.length}/${continentRegions.length} regions`,
            );

            if (validWeatherResults.length > 0) {
              // Calculate aggregated weather statistics
              const tempValues = validWeatherResults
                .map((r) => r.data.temp_c)
                .filter((v) => v != null);
              const humidityValues = validWeatherResults
                .map((r) => r.data.humidity)
                .filter((v) => v != null);
              const precipValues = validWeatherResults
                .map((r) => r.data.precip_mm)
                .filter((v) => v != null);
              const visibilityValues = validWeatherResults
                .map((r) => r.data.vis_km)
                .filter((v) => v != null);
              const pressureValues = validWeatherResults
                .map((r) => r.data.pressure)
                .filter((v) => v != null);
              const windSpeedValues = validWeatherResults
                .map((r) => r.data.wind_speed)
                .filter((v) => v != null);

              const calculateWeatherStats = (values: number[]) => {
                if (values.length === 0)
                  return { mean: 0, min: 0, max: 0, count: 0 };
                const sorted = values.sort((a, b) => a - b);
                const mean =
                  values.reduce((sum, val) => sum + val, 0) / values.length;
                const min = Math.min(...values);
                const max = Math.max(...values);
                return { mean, min, max, count: values.length };
              };

              const aggregatedWeatherData = {
                current: {
                  temp_c:
                    tempValues.length > 0
                      ? calculateWeatherStats(tempValues).mean
                      : 0,
                  precip_mm:
                    precipValues.length > 0
                      ? calculateWeatherStats(precipValues).mean
                      : 0,
                  humidity:
                    humidityValues.length > 0
                      ? calculateWeatherStats(humidityValues).mean
                      : 0,
                  vis_km:
                    visibilityValues.length > 0
                      ? calculateWeatherStats(visibilityValues).mean
                      : 0,
                  pressure:
                    pressureValues.length > 0
                      ? calculateWeatherStats(pressureValues).mean
                      : 0,
                  wind_speed:
                    windSpeedValues.length > 0
                      ? calculateWeatherStats(windSpeedValues).mean
                      : 0,
                },
                statistics: {
                  temperature: calculateWeatherStats(tempValues),
                  humidity: calculateWeatherStats(humidityValues),
                  precipitation: calculateWeatherStats(precipValues),
                  visibility: calculateWeatherStats(visibilityValues),
                  pressure: calculateWeatherStats(pressureValues),
                  windSpeed: calculateWeatherStats(windSpeedValues),
                },
                coverage: {
                  totalRegions: continentRegions.length,
                  regionsWithData: validWeatherResults.length,
                  coveragePercentage: Math.round(
                    (validWeatherResults.length / continentRegions.length) *
                      100,
                  ),
                },
                regionalBreakdown: validWeatherResults.map((r) => ({
                  name: r.regionName,
                  country: r.country,
                  temperature: r.data.temp_c,
                  humidity: r.data.humidity,
                  precipitation: r.data.precip_mm,
                  visibility: r.data.vis_km,
                })),
              };

              setContinentWeatherData(aggregatedWeatherData);
              console.log("🌤️ Continent-wide weather statistics calculated:", {
                coverage: `${validWeatherResults.length}/${continentRegions.length} regions (${aggregatedWeatherData.coverage.coveragePercentage}%)`,
                meanTemp:
                  aggregatedWeatherData.current.temp_c.toFixed(1) + "°C",
                meanHumidity:
                  aggregatedWeatherData.current.humidity.toFixed(1) + "%",
              });
            } else {
              console.warn(
                "🌤️ No valid weather data found for any regions in the continent",
              );
              setContinentWeatherData(null);
            }
          } else {
            console.warn(
              "🌤️ No regions found for continent:",
              selectedContinent,
            );
            setContinentWeatherData(null);
          }
        } catch (weatherError) {
          console.error(
            "🌤️ Error fetching continent weather data:",
            weatherError,
          );
          setContinentWeatherData(null);
        }

        // Fetch air quality data for ALL regions in the continent and calculate aggregates
        try {
          const continentRegions = regions.filter(
            (r) => getContinentFromCountry(r.country) === selectedContinent,
          );
          console.log(
            `🌬️ Fetching air quality data for ${continentRegions.length} regions in ${selectedContinent}`,
          );

          if (continentRegions.length > 0) {
            // Fetch air quality data for all regions in parallel
            const airQualityPromises = continentRegions.map(async (region) => {
              try {
                const data = await fetchRegionAirQuality(region.id);
                return {
                  regionId: region.id,
                  regionName: region.name,
                  country: region.country,
                  data: data,
                };
              } catch (error) {
                console.warn(
                  `Failed to fetch air quality for ${region.name}:`,
                  error,
                );
                return null;
              }
            });

            const airQualityResults = await Promise.all(airQualityPromises);
            const validResults = airQualityResults.filter(
              (result) => result?.data?.aqi && result?.data?.pollutants,
            );

            console.log(
              `🌬️ Successfully retrieved air quality data for ${validResults.length}/${continentRegions.length} regions`,
            );

            if (validResults.length > 0) {
              // Calculate aggregated statistics
              const calculateStats = (values: number[]) => {
                const sorted = values.sort((a, b) => a - b);
                const mean =
                  values.reduce((sum, val) => sum + val, 0) / values.length;
                const median =
                  sorted.length % 2 === 0
                    ? (sorted[sorted.length / 2 - 1] +
                        sorted[sorted.length / 2]) /
                      2
                    : sorted[Math.floor(sorted.length / 2)];
                const min = Math.min(...values);
                const max = Math.max(...values);
                const stdDev = Math.sqrt(
                  values.reduce(
                    (sum, val) => sum + Math.pow(val - mean, 2),
                    0,
                  ) / values.length,
                );
                return {
                  mean,
                  median,
                  min,
                  max,
                  stdDev,
                  count: values.length,
                };
              };

              // Extract pollutant values from all regions
              const pm25Values = validResults
                .map((r) => r.data.pollutants.pm25)
                .filter((v) => v != null);
              const pm10Values = validResults
                .map((r) => r.data.pollutants.pm10)
                .filter((v) => v != null);
              const no2Values = validResults
                .map((r) => r.data.pollutants.no2)
                .filter((v) => v != null);
              const o3Values = validResults
                .map((r) => r.data.pollutants.o3)
                .filter((v) => v != null);

              // Calculate comprehensive statistics
              const aggregatedData = {
                current: {
                  // Use mean values for display (most representative for a continent)
                  pm25:
                    pm25Values.length > 0 ? calculateStats(pm25Values).mean : 0,
                  pm10:
                    pm10Values.length > 0 ? calculateStats(pm10Values).mean : 0,
                  no2:
                    no2Values.length > 0 ? calculateStats(no2Values).mean : 0,
                  o3: o3Values.length > 0 ? calculateStats(o3Values).mean : 0,
                },
                statistics: {
                  pm25:
                    pm25Values.length > 0 ? calculateStats(pm25Values) : null,
                  pm10:
                    pm10Values.length > 0 ? calculateStats(pm10Values) : null,
                  no2: no2Values.length > 0 ? calculateStats(no2Values) : null,
                  o3: o3Values.length > 0 ? calculateStats(o3Values) : null,
                },
                coverage: {
                  totalRegions: continentRegions.length,
                  regionsWithData: validResults.length,
                  coveragePercentage: Math.round(
                    (validResults.length / continentRegions.length) * 100,
                  ),
                },
                regionalBreakdown: validResults.map((r) => ({
                  name: r.regionName,
                  country: r.country,
                  pm25: r.data.pollutants?.pm25 || 0,
                  pm10: r.data.pollutants?.pm10 || 0,
                  no2: r.data.pollutants?.no2 || 0,
                  o3: r.data.pollutants?.o3 || 0,
                })),
              };

              setContinentAirQualityData(aggregatedData);
              console.log(
                "🌬️ Continent-wide air quality statistics calculated:",
                {
                  coverage: `${validResults.length}/${continentRegions.length} regions (${aggregatedData.coverage.coveragePercentage}%)`,
                  meanPM25: aggregatedData.current.pm25.toFixed(1),
                  meanPM10: aggregatedData.current.pm10.toFixed(1),
                },
              );
            } else {
              console.warn(
                "🌬️ No valid air quality data found for any regions in the continent",
              );
              setContinentAirQualityData(null);
            }
          } else {
            console.warn(
              "🌬️ No regions found for continent:",
              selectedContinent,
            );
            setContinentAirQualityData(null);
          }
        } catch (airQualityError) {
          console.error(
            "🌬️ Error fetching continent air quality data:",
            airQualityError,
          );
          setContinentAirQualityData(null);
        }

        // Update previous values for trend calculation
        if (data) {
          setPreviousValues({
            deforestation: data.deforestation.ratePerYear,
            desertification: data.desertification.ratePerYear,
            landLoss: data.landLoss.ratePerYear,
            species: data.endangeredSpecies.count,
          });
        }
      } catch (error) {
        console.error("Error fetching continent data:", error);
        toast({
          title: "Error",
          description: "Failed to load continent data. Please try again.",
          variant: "destructive",
        });
        setContinentData(null);
        setContinentWeatherData(null);
        setContinentAirQualityData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchContinentData();
  }, [selectedContinent, toast]);

  // Tab switching effect for continent selection
  useEffect(() => {
    if (selectedContinent && userInitiatedContinentChange) {
      setActiveTab("regional-analysis");
      scrollToSection("overview-section");
      // Reset the flag after processing
      setUserInitiatedContinentChange(false);
    }
  }, [selectedContinent, userInitiatedContinentChange]);

  // 7. Regions loading effect
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const regionsData = await fetchAllRegions();
        setRegions(regionsData);
        console.log("Loaded regions:", regionsData); // Debug log
      } catch (error) {
        console.error("Failed to load regions:", error);
      }
    };

    loadRegions();
  }, []); // Load regions on component mount

  // 8. Comparison data loading effects
  useEffect(() => {
    const loadComparisonData = async () => {
      if (!selectedRegionForComparison) return;

      try {
        const data = await fetchEnvironmentalData(selectedRegionForComparison);
        setComparisonData(data);
      } catch (error) {
        console.error("Failed to load comparison data:", error);
      }
    };

    loadComparisonData();
  }, [selectedRegionForComparison]);

  useEffect(() => {
    const loadLeftRegionData = async () => {
      if (!selectedLeftRegion) return;

      try {
        const data = await fetchEnvironmentalData(selectedLeftRegion);
        setLeftRegionData(data);
      } catch (error) {
        console.error("Failed to load left region data:", error);
      }
    };

    loadLeftRegionData();
  }, [selectedLeftRegion]);

  useEffect(() => {
    if (!user) return;
    // Fetch saved regions
    supabase
      .from<any, any>("saved_regions")
      .select("id")
      .eq("user_id", user.id)
      .then(({ data }) => {
        setHasSavedRegions((data?.length || 0) > 0);
      });
    // Fetch reports
    supabase
      .from<any, any>("reports")
      .select("id")
      .eq("user_id", user.id)
      .then(({ data }) => {
        setHasReports((data?.length || 0) > 0);
      });
    // Fetch full name from profiles
    supabase
      .from("profiles")
      .select("full_name")
      .eq("id", user.id)
      .single()
      .then(({ data, error }) => {
        if (!error && data && typeof data === "object" && "full_name" in data) {
          setFullName((data as { full_name: string }).full_name);
        }
      });
  }, [user]);

  useEffect(() => {
    if (!user) {
      setIsOnboardingDismissed(false);
      return;
    }

    const dismissed =
      localStorage.getItem(`dashboard_onboarding_dismissed_${user.id}`) ===
      "true";
    setIsOnboardingDismissed(dismissed);
  }, [user]);

  const userFirstName = fullName.trim().split(/\s+/)[0] || "there";
  const onboardingSteps = [
    {
      label: "Choose your first region",
      completed: Boolean(
        selectedContinent || selectedRegion || hasSavedRegions,
      ),
    },
    {
      label: "Generate your first projection",
      completed: Boolean(prediction || hasReports),
    },
  ];
  const completedOnboardingSteps = onboardingSteps.filter(
    (step) => step.completed,
  ).length;
  const shouldShowWelcome =
    !isOnboardingDismissed && completedOnboardingSteps < onboardingSteps.length;

  // Show loading spinner while loading
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-background/80">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#40e0d0]" />
          <p className="text-sm text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Early return if not authenticated
  if (!user) {
    return null;
  }

  // Toggle navbar function
  const toggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen);
  };

  // Add handleSignOut function
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBackToMap = () => {
    navigate("/");
  };

  const performGeneration = async (format: "pdf" | "docx") => {
    // This function executes the original generation flow. It must only be called
    // after the confirmation modal's onConfirm (i.e. user approved and credits sufficiency checked).
    if (!user) {
      setReportMessage("Please log in to generate reports");
      setTimeout(() => setReportMessage(""), 3000);
      return;
    }

    try {
      setIsGeneratingReport(true);
      setReportMessage(`Generating ${format.toUpperCase()} report...`);

      // (same content as previous implementation) Get the current active tab's content and data
      let dashboardElement: HTMLElement | null = null;
      let reportTitle = "";
      let regionName = "";
      let reportPrediction: PredictionResponse | null = null;

      switch (activeTab) {
        case "carbon-tracking":
          dashboardElement = document.getElementById("carbon-tracking-section");
          reportTitle = "Personal Carbon Tracking Report";
          regionName = "Personal Carbon Footprint Analysis";
          reportPrediction = null;
          break;
        case "comparisons":
          dashboardElement = document.getElementById("comparison-section");
          reportTitle = "Regional Comparison Report";
          if (selectedLeftRegion && leftRegionData) {
            regionName = `Environmental Comparison Analysis`;
          } else {
            regionName = "Regional Environmental Comparison";
          }
          reportPrediction = null;
          break;
        case "regional-analysis":
          if (!selectedContinent) {
            setReportMessage("Please select a continent first");
            setTimeout(() => setReportMessage(""), 3000);
            setIsGeneratingReport(false);
            return;
          }
          dashboardElement = document.getElementById("overview-section");
          reportTitle = `${selectedContinent} Environmental Analysis Report`;
          regionName = selectedContinent;
          reportPrediction = null;
          break;
        case "predictions":
          if (!prediction || !selectedRegion) {
            setReportMessage("Please generate a prediction first");
            setTimeout(() => setReportMessage(""), 3000);
            setIsGeneratingReport(false);
            return;
          }
          dashboardElement = document.getElementById("prediction-section");
          reportTitle = `Environmental Impact Prediction Report`;
          regionName = selectedRegion.name;
          reportPrediction = prediction;
          break;
        case "collaboration":
          dashboardElement = document.getElementById("collaboration-section");
          reportTitle = "Project Collaboration Report";
          regionName = selectedRegion?.name || "Project Collaboration";
          reportPrediction = null;
          break;
        default:
          throw new Error("Invalid active tab for report generation");
      }

      if (!dashboardElement && format === "pdf") {
        throw new Error("Dashboard content not found for current tab");
      }

      // Fetch carbon tracking data if generating carbon tracking report
      let carbonTrackingData = null;
      if (activeTab === "carbon-tracking") {
        try {
          const month = summaryMonth || new Date();
          const currentMonth = new Date(
            month.getFullYear(),
            month.getMonth(),
            1,
          );

          // Fetch monthly carbon logs
          const { data: monthlyLogs, error: monthlyError } = await supabase
            .from("carbon_logs")
            .select("*")
            .eq("user_id", user.id)
            .gte("date", currentMonth.toISOString().slice(0, 10))
            .lt(
              "date",
              new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() + 1,
                0,
              )
                .toISOString()
                .slice(0, 10),
            );

          if (monthlyError) throw monthlyError;

          // Fetch previous month data for comparison
          const prevMonth = new Date(
            month.getFullYear(),
            month.getMonth() - 1,
            1,
          );
          const { data: prevMonthLogs, error: prevError } = await supabase
            .from("carbon_logs")
            .select("total_co2")
            .eq("user_id", user.id)
            .gte("date", prevMonth.toISOString().slice(0, 10))
            .lt("date", currentMonth.toISOString().slice(0, 10));

          // Fetch current goal
          const { data: goalData, error: goalError } = await supabase
            .from("carbon_goals")
            .select("*")
            .eq("user_id", user.id)
            .limit(1);

          // Process the data
          const logs = monthlyLogs || [];
          let totalEmissions = 0;
          let foodEmissions = 0;
          let transportEmissions = 0;
          let utilitiesEmissions = 0;
          let goodsEmissions = 0;
          let loggedDays = new Set();

          logs.forEach((log: any) => {
            totalEmissions += log.total_co2 || 0;
            loggedDays.add(log.date);

            // Calculate category emissions
            if (log.food && Array.isArray(log.food)) {
              log.food.forEach((item: any) => {
                foodEmissions += (item.servings || 0) * 2.5; // Rough estimate
              });
            }

            if (log.transport && Array.isArray(log.transport)) {
              log.transport.forEach((item: any) => {
                transportEmissions += (item.distance || 0) * 0.2; // Rough estimate per km
              });
            }

            utilitiesEmissions +=
              (log.home_electricity || 0) * 0.4 +
              (log.home_gas || 0) * 2.0 +
              (log.home_water || 0) * 0.001;

            goodsEmissions += log.goods_services || 0;
          });

          const previousMonthTotal = (prevMonthLogs || []).reduce(
            (sum: number, log: any) => sum + (log.total_co2 || 0),
            0,
          );

          const currentGoal = goalData?.[0]?.target_amount || null;

          carbonTrackingData = {
            monthlyLogs: logs,
            totalEmissions,
            categoryBreakdown: {
              food: foodEmissions,
              transport: transportEmissions,
              utilities: utilitiesEmissions,
              goods: goodsEmissions,
            },
            previousMonthTotal,
            currentGoal,
            loggedDays: loggedDays.size,
            month,
          };
        } catch (error) {
          console.error(
            "Error fetching carbon tracking data for report:",
            error,
          );
          // Continue with null data - will generate a message about data not being available
        }
      }

      // Fetch regional carbon data if available for regional analysis
      let regionalCarbonData = null;
      if (activeTab === "regional-analysis" && selectedRegion) {
        try {
          const { getRegionCarbonData } =
            await import("@/services/carbonService");
          regionalCarbonData = await getRegionCarbonData(selectedRegion.id);
        } catch (error) {
          console.error(
            "Error fetching regional carbon data for report:",
            error,
          );
        }
      }

      // For Regional Analysis reports, create filtered continent data that excludes mock tables
      let filteredContinentData = continentData;
      if (activeTab === "regional-analysis" && continentData) {
        // Create a copy without problematic mock data sections
        filteredContinentData = { ...continentData };
        // Note: The actual filtering happens in the report generator's table processing
        // This flag helps the generator know to skip mock data tables
        (filteredContinentData as any).excludeMockData = true;
      }

      const reportData: ReportData = {
        title: reportTitle,
        regionName: regionName,
        prediction: reportPrediction,
        timeframe: predictionTimeframe,
        subscriptionTier: subscriptionTier,
        generatedDate: new Date().toLocaleString(),
        username: user.email || user.user_metadata?.name || undefined,
        activeTab: activeTab,
        continentData:
          activeTab === "regional-analysis"
            ? filteredContinentData
            : activeTab === "predictions"
              ? currentEnvData // ✅ Use currentEnvData for predictions tab
              : null,
        comparisonData:
          activeTab === "comparisons"
            ? {
                leftRegion: selectedLeftRegion,
                leftData: leftRegionData,
                rightRegion: selectedRegionForComparison,
                rightData: comparisonData,
              }
            : null,
        carbonTrackingData: carbonTrackingData,
        regionalCarbonData: regionalCarbonData,
      };

      let blob: Blob;
      let fileName: string;
      let contentType: string;

      if (format === "docx") {
        blob = await ReportGenerator.generateWordDocument(reportData);
        fileName = `${reportTitle.toLowerCase().replace(/\s+/g, "_")}_${Date.now()}.docx`;
        contentType =
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      } else {
        blob = await ReportGenerator.generateEnhancedPDF(reportData);
        fileName = `${reportTitle.toLowerCase().replace(/\s+/g, "_")}_${Date.now()}.pdf`;
        contentType = "application/pdf";
      }

      // For accounting traceability generate jobId and creditsRequired before persistence
      const jobId =
        crypto && typeof (crypto as any).randomUUID === "function"
          ? (crypto as any).randomUUID()
          : `${Date.now()}-${Math.random()}`;
      const creditsRequired = getCreditCost(
        "STANDARD_REPORT" as unknown as JobType,
      );

      // Save to Supabase storage
      const filePath = `${user.id}/${fileName}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("reports")
        .upload(filePath, blob, {
          contentType: contentType,
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error("Error uploading to storage:", uploadError);
        throw uploadError;
      }

      // Get the public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("reports").getPublicUrl(filePath);

      // Save report metadata using the service
      const saved = await saveReport(
        user.id,
        regionName || "Global",
        publicUrl,
        fileName,
      );
      if (!saved) throw new Error("Failed to save report metadata");

      // After successful job and persistence, call backend consume endpoint with explicit creditsRequired
      try {
        const consumeResult = await consumeCredits(
          user.id,
          "STANDARD_REPORT" as unknown as JobType,
          jobId,
          creditsRequired,
        );
        if (!consumeResult.success) {
          console.error("Credit consumption failed:", consumeResult.error);
          // Job succeeded, but billing failed — inform user but do not roll back output
          toast({
            title: "Report generated (billing issue)",
            description:
              "Report was generated but we could not deduct credits. Please contact support.",
            variant: "destructive",
          });
        }
        // Always refresh balances from backend and broadcast to UI listeners so the
        // CreditBalanceDisplay and others show the latest state even on failures.
        try {
          const balances = await getCreditBalances(user.id);
          creditBalanceEvent.dispatchEvent(
            new CustomEvent("updated", {
              detail: { regular: balances.regular, bonus: balances.bonus },
            }),
          );
        } catch (err) {
          console.error(
            "Failed to refresh balances after consume attempt:",
            err,
          );
        }
      } catch (err) {
        console.error("Error calling consumeCredits:", err);
      }

      // Update report limit UI
      const updatedLimitInfo = await canGenerateReport(user);
      setReportLimit(updatedLimitInfo);

      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setReportMessage(
        `${format.toUpperCase()} report generated successfully! ${subscriptionTier === "PAYG" ? updatedLimitInfo.message : ""}`,
      );
      setTimeout(() => setReportMessage(""), 5000);
    } catch (error) {
      console.error("Error generating report:", error);
      setReportMessage(
        `Error generating ${format.toUpperCase()} report. Please try again.`,
      );
      setTimeout(() => setReportMessage(""), 3000);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const generateReport = async (format: "pdf" | "docx" = "pdf") => {
    // Before running generation, request credit confirmation modal which checks sufficiency
    requestConfirmation(
      "STANDARD_REPORT" as unknown as JobType,
      async () => {
        await performGeneration(format);
      },
      { title: `Generate Report` },
    );
  };

  // Add this function to handle closing the prediction panel
  const handleClosePrediction = () => {
    setShowPrediction(false);
    localStorage.removeItem("currentPrediction");
  };

  const handleSavePrediction = async () => {
    if (!prediction || !selectedRegion) return;
    const label = `${selectedRegion.name} — ${new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}`;
    try {
      // Enforce save limit
      const current = await countSavedPredictions();
      const max = SAVED_PREDICTION_LIMITS[subscriptionTier] ?? 10;
      if (current >= max) {
        toast({
          title: "Limit reached",
          description: `You've used all ${max} saved projection slots. Delete some from History to free up space.`,
          variant: "destructive",
        });
        return;
      }
      await savePrediction({
        region_id: selectedRegion.id,
        region_name: selectedRegion.name,
        region_continent: selectedRegion.continent,
        timeframe: predictionTimeframe,
        label,
        predictions: prediction.predictions as unknown as Record<
          string,
          unknown
        >,
      });
      toast({ title: "Saved", description: "Prediction saved to History." });
    } catch {
      toast({
        title: "Error",
        description: "Could not save prediction.",
        variant: "destructive",
      });
    }
  };

  const handleLoadFromHistory = (saved: SavedPrediction) => {
    const region: Region = {
      id: saved.region_id,
      name: saved.region_name,
      coordinates: [0, 0],
      country: "",
      continent: "",
      latitude: undefined,
      longitude: undefined,
    };
    setPrediction({
      region_id: saved.region_id,
      predictions: saved.predictions as unknown as PredictionValues,
      timestamp: saved.created_at,
    });
    setSelectedRegion(region);
    setPredictionTimeframe(saved.timeframe);
    setShowPrediction(true);
    setActiveTab("predictions");
  };

  // Add this function to handle region change and prediction generation
  const handleGeneratePrediction = async (regionName: string) => {
    if (!regionName) return;

    // Check if user has access to AI predictions
    if (!hasAccess("aiInsights")) {
      toast({
        title: "Premium Feature",
        description: getRestrictionMessage("aiInsights", "PAYG"),
        variant: "destructive",
      });
      return;
    }

    // Ask for credit confirmation before running the prediction
    // Use the appropriate JobType for the selected timeframe
    requestConfirmation(
      getPredictionJobType("10y"),
      async () => {
        try {
          setPredictionLoading(true);

          // Find the region data
          const region = regions.find((r) => r.name === regionName);
          if (!region) {
            toast({
              title: "Error",
              description: "Region not found",
              variant: "destructive",
            });
            return;
          }

          // Get environmental data for the region using region ID
          const envData = await fetchEnvironmentalData(region.id);

          if (!envData) {
            toast({
              title: "Error",
              description: "No environmental data available for this region",
              variant: "destructive",
            });
            return;
          }

          // cache current env data for visualizations (do before generating prediction)
          setCurrentEnvData(envData);

          // Generate prediction using selected timeframe (no user tier restriction)
          const predictionResult = await generatePrediction(
            region.id,
            envData,
            "10y",
            user?.id,
          );

          if (predictionResult) {
            // The prediction API performs credit deduction server-side.
            // Avoid client-side `consumeCredits` here to prevent double-charging.
            setPrediction(predictionResult);
            setShowPrediction(true);
            setSelectedRegion(region);
            // Switch to predictions tab when a prediction is generated
            setActiveTab("predictions");
            // Show save hint tooltip for 3 seconds
            setShowSaveHint(true);
            setTimeout(() => setShowSaveHint(false), 3000);
            toast({
              title: "Success",
              description: `Prediction generated for ${regionName}`,
              variant: "default",
            });
          } else {
            toast({
              title: "Error",
              description: "Failed to generate prediction",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error("Error generating prediction:", error);
          toast({
            title: "Error",
            description: "Error generating prediction. Please try again.",
            variant: "destructive",
          });
        } finally {
          setPredictionLoading(false);
        }
      },
      {
        title: `Generate Prediction`,
        description: `This will generate a prediction for ${regionName}`,
      },
    );
  };

  // Handle compare regions button click with tab navigation
  const handleCompareRegions = () => {
    setActiveTab("comparisons");

    // Show feedback toast
    toast({
      title: "Comparison Mode Activated",
      description: "Switching to comparison tab...",
      variant: "default",
    });

    // Scroll to comparison section
    scrollToSection("comparison-section");
  };
  const scrollToSection = (sectionId: string) => {
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        const header = document.querySelector("header");
        const headerHeight = header ? header.offsetHeight : 80;
        const elementTop = section.offsetTop;
        const offsetPosition = elementTop - headerHeight - 20;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  // Handle tab navigation changes
  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);

    // Auto-scroll to the corresponding section
    const sectionIds = {
      "carbon-tracking": "carbon-tracking-section",
      comparisons: "comparison-section",
      "regional-analysis": "overview-section",
      predictions: "prediction-section",
      collaboration: "collaboration-section",
    };

    const targetSectionId = sectionIds[tabValue as keyof typeof sectionIds];
    if (targetSectionId) {
      scrollToSection(targetSectionId);
    }
  };

  // Don't render anything if user is not authenticated
  if (!user) {
    return null;
  }

  const continents = [
    "Africa",
    "Antarctica",
    "Asia",
    "Europe",
    "North America",
    "Oceania",
    "South America",
  ];

  // Transform historical data into pie chart format
  const transformDataForPie = (data: any[], key: string) => {
    return data.map((item) => ({
      name: item.year.toString(),
      value: item[key],
    }));
  };

  // Process data for the bar chart
  const processBarChartData = () => {
    if (!continentData?.endangeredSpecies?.topThreatenedSpecies) return [];

    const speciesMap = new Map<
      string,
      { name: string; population: number; status: string }
    >();

    continentData.endangeredSpecies.topThreatenedSpecies.forEach((species) => {
      const population =
        typeof species.population === "number" ? species.population : 0;
      const existingSpecies = speciesMap.get(species.name);

      if (existingSpecies) {
        existingSpecies.population += population;
      } else {
        speciesMap.set(species.name, {
          name: species.name,
          population: population,
          status: species.status,
        });
      }
    });

    return Array.from(speciesMap.values())
      .sort((a, b) => b.population - a.population)
      .slice(0, 10);
  };

  return (
    <>
      <SEO
        title="Carbon Footprint Tracker & Environmental Dashboard"
        description="Use Myhu's carbon footprint tracker to measure emissions, set reduction goals, and celebrate sustainability wins."
        keywords={[
          "carbon footprint tracker",
          "environmental impact analytics",
          "sustainable lifestyle planner",
          "eco-monitoring platform",
          "real-time biodiversity monitoring",
          "community-driven conservation technology",
        ]}
        canonicalUrl="https://myhu.world/dashboard"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Environmental Dashboard - Myhu",
          description:
            "Track your carbon footprint and environmental impact with Myhu's comprehensive dashboard",
          url: "https://myhu.world/dashboard",
          isPartOf: {
            "@type": "WebSite",
            name: "Myhu",
            url: "https://myhu.world",
          },
        }}
      />
      <div className="min-h-screen flex bg-gradient-to-b from-background to-background/80">
        {/* Collapsible Navbar */}
        <Sidebar />

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <TopBar
            title="Dashboard"
            icon={<BarChart2 className="h-4 w-4 text-[#40e0d0]" />}
            showBackButton={false}
            showSubscriptionBadge={true}
          />

          {/* Modern Action Bar */}
          <div className=" border-b border-border/20 backdrop-blur-sm">
            <div className="container mx-auto px-4 sm:px-6 py-6">
              {/* Primary Action Row */}
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                {/* Left: Tab Navigation */}
                <div className="flex-1">
                  <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-border/40 p-1 rounded-xl shadow-lg flex">
                    <button
                      onClick={() => handleTabChange("carbon-tracking")}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-all rounded-lg ${
                        activeTab === "carbon-tracking"
                          ? "bg-[#40e0d0] text-white dark:text-black shadow-md"
                          : "text-muted-foreground dark:text-white/50 hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      <Activity className="h-4 w-4" />
                      Carbon Tracking
                    </button>
                    <button
                      onClick={() => handleTabChange("regional-analysis")}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-all rounded-lg ${
                        activeTab === "regional-analysis"
                          ? "bg-[#40e0d0] text-white dark:text-black shadow-md"
                          : "text-muted-foreground dark:text-white/50 hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      <Globe className="h-4 w-4" />
                      Regional Analysis
                    </button>
                    <button
                      onClick={() => handleTabChange("comparisons")}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-all rounded-lg ${
                        activeTab === "comparisons"
                          ? "bg-[#40e0d0] text-white dark:text-black shadow-md"
                          : "text-muted-foreground dark:text-white/50 hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      <GitCompareArrows className="h-4 w-4" />
                      Comparisons
                    </button>
                    <button
                      onClick={() => handleTabChange("predictions")}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-all rounded-lg ${
                        activeTab === "predictions"
                          ? "bg-[#40e0d0] text-white dark:text-black shadow-md"
                          : "text-muted-foreground dark:text-white/50 hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      <Brain className="h-4 w-4" />
                      Projections
                    </button>
                    <button
                      onClick={() => handleTabChange("collaboration")}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-all rounded-lg ${
                        activeTab === "collaboration"
                          ? "bg-[#40e0d0] text-white dark:text-black shadow-md"
                          : "text-muted-foreground dark:text-white/50 hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      <Users className="h-4 w-4" />
                      Collaboration
                    </button>
                  </div>
                </div>

                {/* Right: Quick Actions */}
                <div className="flex items-start gap-3">
                  {/* Download Report with Format Options */}
                  <div className="relative">
                    <DropdownMenu>
                      <UITooltip>
                        <UITooltipTrigger asChild>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              disabled={
                                isGeneratingReport ||
                                activeTab === "collaboration"
                              }
                              className="flex items-center gap-2 border-[#40e0d0]/30 text-[#40e0d0] bg-white/50 dark:bg-[#1c2e2b] text-muted-foreground backdrop-blur-sm hover:bg-[#40e0d0]/10 hover:border-[#40e0d0] shadow-md"
                            >
                              <Download className="h-4 w-4" />
                              {isGeneratingReport
                                ? "Generating..."
                                : "Download Report"}
                              {!isGeneratingReport && (
                                <ChevronDown className="h-3 w-3 ml-1" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                        </UITooltipTrigger>
                        <UITooltipContent>
                          <div className="max-w-xs">
                            <div className="font-medium">{`This will use ${getCreditCost("STANDARD_REPORT")} credit${getCreditCost("STANDARD_REPORT") > 1 ? "s" : ""}`}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              This action will generate a new output and use{" "}
                              {getCreditCost("STANDARD_REPORT")} credit
                              {getCreditCost("STANDARD_REPORT") > 1 ? "s" : ""}.
                              Credits are deducted only after successful
                              completion.
                            </div>
                          </div>
                        </UITooltipContent>
                      </UITooltip>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel>Report Format</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => generateReport("pdf")}
                          disabled={isGeneratingReport}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <FileText className="h-4 w-4 text-red-500" />
                          <div className="flex flex-col">
                            <span className="font-medium">PDF Document</span>
                            <span className="text-xs text-muted-foreground">
                              Includes dashboard screenshot
                            </span>
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => generateReport("docx")}
                          disabled={isGeneratingReport}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <FileText className="h-4 w-4 text-blue-500" />
                          <div className="flex flex-col">
                            <span className="font-medium">Word Document</span>
                            <span className="text-xs text-muted-foreground">
                              Structured report with tables
                            </span>
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {/* report counter removed — no longer relevant for PAYG */}
                  </div>
                </div>
              </div>

              {/* Context-Aware Secondary Actions */}
              {activeTab === "regional-analysis" && (
                <div className="mt-4 pt-4 border-t border-border/20">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                    <div className="relative">
                      {/* Glowing Border Effect */}
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-[#40e0d0] to-[#00bcd4] rounded-xl blur opacity-20"></div>
                      <div className="relative flex items-center gap-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-[#40e0d0]/20">
                        <Select
                          value={selectedContinent}
                          onValueChange={(value) => {
                            setSelectedContinent(value);
                            setUserInitiatedContinentChange(true);
                          }}
                        >
                          <SelectTrigger className="w-[240px] border-border/40 bg-transparent">
                            <SelectValue placeholder="Select a continent" />
                          </SelectTrigger>
                          <SelectContent>
                            {continents.map((continent) => (
                              <SelectItem key={continent} value={continent}>
                                {continent}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-xs text-muted-foreground hidden md:block">
                          Environmental data by continent
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Report message display */}
          {isGeneratingReport && (
            <p className="text-sm text-muted-foreground animate-pulse text-center">
              {reportMessage}
            </p>
          )}

          {/* Main Content */}
          <main className="flex-1 container mx-auto px-4 sm:px-6 py-8">
            {/* Welcome Section */}
            {shouldShowWelcome && (
              <section className="bg-[#40e0d0]/10 dark:bg-[#272727] backdrop-blur-sm rounded-xl border border-border/40 p-6 mt-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                  <div className="lg:col-span-2 space-y-4">
                    <Badge variant="outline" className="text-[#40e0d0]">
                      New User Setup
                    </Badge>
                    <h2 className="text-2xl sm:text-3xl font-semibold text-foreground leading-tight">
                      Welcome, {userFirstName}. Start your first impact
                      snapshot.
                    </h2>
                    <p className="text-muted-foreground max-w-2xl">
                      Pick a region to unlock live environmental analysis, then
                      run your first projection. Setup takes about 2 minutes.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 pt-2">
                      <Button
                        onClick={() => {
                          setActiveTab("regional-analysis");
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="bg-[#40e0d0] hover:bg-[#40e0d0]/80 text-white"
                      >
                        Start with Regional Analysis
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setActiveTab("carbon-tracking");
                          setShowCarbonModal(true);
                        }}
                        className="border-[#40e0d0] text-[#40e0d0] hover:bg-[#40e0d0]/10"
                      >
                        Add First Carbon Entry
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setIsOnboardingDismissed(true);
                          if (user?.id) {
                            localStorage.setItem(
                              `dashboard_onboarding_dismissed_${user.id}`,
                              "true",
                            );
                          }
                        }}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>

                  <Card className="border-border/40 bg-background/70">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base font-semibold">
                        Setup Progress ({completedOnboardingSteps}/
                        {onboardingSteps.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {onboardingSteps.map((step) => (
                        <div
                          key={step.label}
                          className="flex items-center gap-3 text-sm"
                        >
                          <div
                            className={`w-5 h-5 rounded-full flex items-center justify-center ${
                              step.completed
                                ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {step.completed ? (
                              <Check className="h-3.5 w-3.5" />
                            ) : (
                              <Cloud className="h-3 w-3" />
                            )}
                          </div>
                          <span
                            className={
                              step.completed
                                ? "text-foreground"
                                : "text-muted-foreground"
                            }
                          >
                            {step.label}
                          </span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
              </section>
            )}

            {/* Tab Content Sections */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full mt-8"
            >
              {/* Carbon Tracking Tab Content */}
              <TabsContent value="carbon-tracking" className="space-y-6">
                {/* Carbon Tracking & Progress Section - Modernized */}
                <section
                  id="carbon-tracking-section"
                  className="bg-[#f3f4f6]/20 dark:bg-[#272727] backdrop-blur-sm rounded-lg border border-border/40 p-6"
                >
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-semibold text-foreground">
                      Carbon Tracking & Progress
                    </h2>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (!hasAccess("aiSummary")) {
                            toast({
                              title: "Feature Restricted",
                              description: getRestrictionMessage("aiSummary"),
                              variant: "destructive",
                            });
                            return;
                          }

                          const section = document.getElementById(
                            "carbon-widgets-section",
                          );
                          if (section) {
                            // Trigger the summarize functionality from ProgressView
                            const progressView = section.querySelector(
                              "[data-progress-view]",
                            );
                            if (progressView) {
                              const event = new CustomEvent("triggerSummary");
                              progressView.dispatchEvent(event);
                            }
                          }
                        }}
                        className={`border-[#40e0d0] text-[#40e0d0] hover:bg-[#40e0d0]/10 flex items-center gap-2 ${
                          !hasAccess("aiSummary") ? "opacity-50" : ""
                        }`}
                        title="Generate AI summary of your carbon progress"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        AI Summary
                        {!hasAccess("aiSummary") && (
                          <span className="text-xs opacity-75 ml-1">
                            (Premium)
                          </span>
                        )}
                      </Button>

                      {/* Telegram Integration */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (!hasAccess("whatsappTracking")) {
                            toast({
                              title: "Feature Restricted",
                              description:
                                getRestrictionMessage("whatsappTracking"),
                              variant: "destructive",
                            });
                            return;
                          }
                          setShowTelegramModal(true);
                        }}
                        className={`bg-[#229ED9]/90 hover:bg-[#fff] hover:text-[#229ED9] dark:bg-[#229ED9]/90 dark:hover:bg-[#fff] text-white font-medium flex items-center gap-2 ${
                          !hasAccess("whatsappTracking") ? "opacity-50" : ""
                        }`}
                        title="Track carbon footprint via Telegram"
                      >
                        <Send className="h-4 w-4" />
                        Telegram
                        {!hasAccess("whatsappTracking") && (
                          <span className="text-xs opacity-75 ml-1">
                            (Premium)
                          </span>
                        )}
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCarbonModal(true)}
                        className="border-[#40e0d0] text-[#40e0d0] hover:bg-[#40e0d0]/10"
                      >
                        + Add Entry
                      </Button>
                    </div>
                  </div>

                  <div id="carbon-widgets-section">
                    {/* Hero Carbon Metrics */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                      {/* Monthly Footprint - Primary KPI */}
                      <Card className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-[#40e0d0]">
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold text-foreground">
                              Monthly Carbon Footprint
                            </CardTitle>
                            <button
                              onClick={() => setShowDailyLogModal(true)}
                              className="w-10 h-10 rounded-lg bg-[#40e0d0]/10 font-bold hover:bg-[#40e0d0] hover:text-white transition flex items-center justify-center text-[#40e0d0]"
                              title="Edit Carbon Log"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            </button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <CarbonSummaryWidget
                              onGetStarted={() => setShowCarbonModal(true)}
                              onCardClick={(category) =>
                                setDrilldownCategory(category)
                              }
                              month={summaryMonth}
                              setMonth={setSummaryMonth}
                              refreshTrigger={refreshTrigger}
                              onDataSaved={triggerRefresh}
                            />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Progress Tracking - Primary KPI */}
                      <Card className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-green-500">
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold text-foreground">
                              Goal Progress
                            </CardTitle>
                            <button
                              onClick={() => {
                                // Trigger the goal editing functionality from ProgressView
                                const progressView = document.querySelector(
                                  "[data-progress-view]",
                                );
                                if (progressView) {
                                  const event = new CustomEvent(
                                    "triggerGoalEdit",
                                  );
                                  progressView.dispatchEvent(event);
                                }
                              }}
                              className="w-10 h-10 rounded bg-green-500/10 font-bold hover:bg-green-500 hover:text-white transition flex items-center justify-center text-green-500"
                              title="Edit Goal"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            </button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <ProgressView
                              refreshTrigger={refreshTrigger}
                              month={summaryMonth}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Carbon Trends Visualization */}
                    <Card className="mb-8">
                      <CardHeader>
                        <CardTitle className="text-lg font-semibold">
                          Carbon Emission Trends
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                          Track your weekly emissions patterns and identify
                          improvement opportunities
                        </p>
                      </CardHeader>
                      <CardContent>
                        <CarbonTrendChart
                          month={summaryMonth}
                          refreshTrigger={refreshTrigger}
                        />
                      </CardContent>
                    </Card>
                  </div>
                </section>
                <CarbonDrilldownModal
                  open={!!drilldownCategory}
                  onClose={() => setDrilldownCategory(null)}
                  category={drilldownCategory || ""}
                  month={summaryMonth}
                />
              </TabsContent>

              {/* Credit Confirmation Modal (shared) */}
              <CreditConfirmationModal
                open={modalState.open}
                onOpenChange={(open) => {
                  if (!open) closeModal();
                }}
                jobType={modalState.jobType as any}
                userId={user?.id}
                onConfirm={modalState.onConfirm || (async () => {})}
                title={modalState.title}
                description={modalState.description}
              />

              {/* Comparisons Tab Content */}
              <TabsContent value="comparisons" className="space-y-6">
                {/* Region Comparison Section - Now always visible when tab is active */}
                <section
                  id="comparison-section"
                  className="bg-[#f3f4f6]/20 dark:bg-[#272727]  backdrop-blur-sm rounded-lg border border-border/40 p-6 mb-8"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-foreground">
                      Region Comparison
                    </h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedLeftRegion(null);
                        setLeftRegionData(null);
                        setSelectedRegionForComparison(null);
                        setComparisonData(null);
                      }}
                      title="Clear all selections"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Region Panel */}
                    <div className="space-y-4">
                      <Popover
                        open={leftRegionOpen}
                        onOpenChange={setLeftRegionOpen}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={leftRegionOpen}
                            className="w-full justify-between font-normal"
                          >
                            {selectedLeftRegion
                              ? (() => {
                                  const r = regions.find(
                                    (r) => r.id === selectedLeftRegion,
                                  );
                                  return r
                                    ? `${r.name} (${r.country})`
                                    : "Select first region";
                                })()
                              : "Select first region"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[--radix-popover-trigger-width] p-0"
                          align="start"
                        >
                          <Command>
                            <CommandInput placeholder="Search region..." />
                            <CommandList>
                              <CommandEmpty>
                                Loading regional data...
                              </CommandEmpty>
                              <CommandGroup>
                                {regions.map((region) => (
                                  <CommandItem
                                    key={region.id}
                                    value={`${region.name} ${region.country}`}
                                    onSelect={() => {
                                      setSelectedLeftRegion(region.id);
                                      setLeftRegionOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${
                                        selectedLeftRegion === region.id
                                          ? "opacity-100"
                                          : "opacity-0"
                                      }`}
                                    />
                                    {region.name} ({region.country})
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>

                      {selectedLeftRegion && leftRegionData ? (
                        <div className="min-h-[400px]">
                          <ComparisonPanel
                            regionId={selectedLeftRegion}
                            data={leftRegionData}
                          />
                        </div>
                      ) : selectedLeftRegion && !leftRegionData ? (
                        <div className="flex flex-col items-center justify-center p-8 rounded-lg border border-dashed border-border bg-card min-h-[400px] text-center space-y-4">
                          <AlertTriangle className="h-16 w-16 text-orange-500" />
                          <div>
                            <h3 className="text-[3rem] font-semibold mb-2">
                              No Data Available
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              Environmental data for this region is not
                              available at the moment.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center p-8 rounded-lg border border-dashed border-border bg-card min-h-[400px]">
                          <p className="text-muted-foreground">
                            Select first region to compare
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Right Region Panel */}
                    <div className="space-y-4">
                      <Popover
                        open={rightRegionOpen}
                        onOpenChange={setRightRegionOpen}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={rightRegionOpen}
                            className="w-full justify-between font-normal"
                          >
                            {selectedRegionForComparison
                              ? (() => {
                                  const r = regions.find(
                                    (r) => r.id === selectedRegionForComparison,
                                  );
                                  return r
                                    ? `${r.name} (${r.country})`
                                    : "Select second region";
                                })()
                              : "Select second region"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[--radix-popover-trigger-width] p-0"
                          align="start"
                        >
                          <Command>
                            <CommandInput placeholder="Search region..." />
                            <CommandList>
                              <CommandEmpty>
                                Loading regional data...
                              </CommandEmpty>
                              <CommandGroup>
                                {regions.map((region) => (
                                  <CommandItem
                                    key={region.id}
                                    value={`${region.name} ${region.country}`}
                                    onSelect={() => {
                                      setSelectedRegionForComparison(region.id);
                                      setRightRegionOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${
                                        selectedRegionForComparison ===
                                        region.id
                                          ? "opacity-100"
                                          : "opacity-0"
                                      }`}
                                    />
                                    {region.name} ({region.country})
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>

                      {selectedRegionForComparison && comparisonData ? (
                        <div className="min-h-[400px]">
                          <ComparisonPanel
                            regionId={selectedRegionForComparison}
                            data={comparisonData}
                          />
                        </div>
                      ) : selectedRegionForComparison && !comparisonData ? (
                        <div className="flex flex-col items-center justify-center p-8 rounded-lg border border-dashed border-border bg-card min-h-[400px] text-center space-y-4">
                          <AlertTriangle className="h-16 w-16 text-muted-foreground/50" />
                          <div>
                            <h3 className="text-lg font-medium mb-2">
                              No Data Available
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              Environmental data for this region is not
                              available at the moment.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center p-8 rounded-lg border border-dashed border-border bg-card min-h-[400px]">
                          <p className="text-muted-foreground">
                            Select second region to compare
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </TabsContent>

              {/* Regional Analysis Tab Content */}
              <TabsContent value="regional-analysis" className="space-y-6">
                {/* Continent Overview Section */}
                {selectedContinent ? (
                  <section
                    id="overview-section"
                    className="bg-[#f3f4f6]/20 dark:bg-[#272727] backdrop-blur-sm rounded-lg border border-border/40 p-6 relative"
                  >
                    <div className="flex items-center justify-between mb-8">
                      <h2 className="text-2xl font-semibold text-foreground text-left">
                        {selectedContinent} Overview
                      </h2>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-4 top-4"
                        onClick={() => {
                          setSelectedContinent("");
                          setUserInitiatedContinentChange(false);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {continentData ? (
                      <>
                        {/* Hero Metrics - Primary KPIs */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                          {/* Deforestation Rate - Primary KPI */}
                          <Card className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-red-500">
                            <CardHeader className="pb-4">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg font-semibold text-foreground">
                                  Forest Loss Rate
                                </CardTitle>
                                <div className="p-2 rounded-lg bg-red-500/10">
                                  <svg
                                    className="w-6 h-6 text-red-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-4">
                                <div className="flex items-baseline space-x-2">
                                  <span className="text-3xl font-bold text-foreground">
                                    {continentData.deforestation.ratePerYear.toFixed(
                                      2,
                                    )}
                                    %
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    annually
                                  </span>
                                  <div
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      getTrend(
                                        continentData.deforestation.ratePerYear,
                                        previousValues.deforestation,
                                      ) === "increasing"
                                        ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                        : getTrend(
                                              continentData.deforestation
                                                .ratePerYear,
                                              previousValues.deforestation,
                                            ) === "decreasing"
                                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                          : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                                    }`}
                                  >
                                    {getTrend(
                                      continentData.deforestation.ratePerYear,
                                      previousValues.deforestation,
                                    )}
                                  </div>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                  <div
                                    className="bg-red-500 h-2.5 rounded-full trend-bar"
                                    style={{
                                      // eslint-disable-next-line
                                      ["--trend-bar-width" as any]: `${Math.min(continentData.deforestation.ratePerYear * 10, 100)}%`,
                                    }}
                                  ></div>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  Impact on total land area with{" "}
                                  {getTrend(
                                    continentData.deforestation.ratePerYear,
                                    previousValues.deforestation,
                                  )}{" "}
                                  trend
                                </p>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Threatened Species - Primary KPI */}
                          <Card className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-amber-500">
                            <CardHeader className="pb-4">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg font-semibold text-foreground">
                                  Threatened Species
                                </CardTitle>
                                <div className="p-2 rounded-lg bg-amber-500/10">
                                  <svg
                                    className="w-6 h-6 text-amber-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-4">
                                <div className="flex items-baseline space-x-2">
                                  <span className="text-3xl font-bold text-foreground">
                                    {continentData.endangeredSpecies.count}
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    species
                                  </span>
                                  <div
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      continentData.endangeredSpecies.count >
                                      100
                                        ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                        : continentData.endangeredSpecies
                                              .count > 50
                                          ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                    }`}
                                  >
                                    {continentData.endangeredSpecies.count > 100
                                      ? "Critical"
                                      : continentData.endangeredSpecies.count >
                                          50
                                        ? "High"
                                        : "Moderate"}
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center">
                                  <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                                    <div className="text-lg font-semibold text-red-600">
                                      {Math.floor(
                                        continentData.endangeredSpecies.count *
                                          0.3,
                                      )}
                                    </div>
                                    <div className="text-xs text-red-600">
                                      Critical
                                    </div>
                                  </div>
                                  <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                                    <div className="text-lg font-semibold text-amber-600">
                                      {Math.floor(
                                        continentData.endangeredSpecies.count *
                                          0.4,
                                      )}
                                    </div>
                                    <div className="text-xs text-amber-600">
                                      Endangered
                                    </div>
                                  </div>
                                  <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                                    <div className="text-lg font-semibold text-green-600">
                                      {Math.floor(
                                        continentData.endangeredSpecies.count *
                                          0.3,
                                      )}
                                    </div>
                                    <div className="text-xs text-green-600">
                                      Vulnerable
                                    </div>
                                  </div>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  Biodiversity under threat with conservation
                                  efforts in progress
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                        {/* Secondary Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                          {/* Desertification Rate - Secondary KPI */}
                          <Card className="hover:shadow-md transition-shadow duration-200">
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
                                <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                                Desertification Rate
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-2xl font-bold text-foreground">
                                    {continentData.desertification.ratePerYear.toFixed(
                                      2,
                                    )}
                                    %
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {getTrend(
                                      continentData.desertification.ratePerYear,
                                      previousValues.desertification,
                                    )}{" "}
                                    trend
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm text-muted-foreground">
                                    vs. global avg
                                  </div>
                                  <div
                                    className={`text-sm font-medium ${
                                      continentData.desertification
                                        .ratePerYear > 2
                                        ? "text-red-500"
                                        : "text-green-500"
                                    }`}
                                  >
                                    {continentData.desertification.ratePerYear >
                                    2
                                      ? "+"
                                      : "-"}
                                    {Math.abs(
                                      continentData.desertification
                                        .ratePerYear - 1.5,
                                    ).toFixed(1)}
                                    %
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Land Loss Rate - Secondary KPI */}
                          <Card className="hover:shadow-md transition-shadow duration-200">
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
                                <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                                Land Loss Rate
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-2xl font-bold text-foreground">
                                    {continentData.landLoss.ratePerYear.toFixed(
                                      2,
                                    )}
                                    %
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {getTrend(
                                      continentData.landLoss.ratePerYear,
                                      previousValues.landLoss,
                                    )}{" "}
                                    trend
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm text-muted-foreground">
                                    vs. global avg
                                  </div>
                                  <div
                                    className={`text-sm font-medium ${
                                      continentData.landLoss.ratePerYear > 1
                                        ? "text-red-500"
                                        : "text-green-500"
                                    }`}
                                  >
                                    {continentData.landLoss.ratePerYear > 1
                                      ? "+"
                                      : "-"}
                                    {Math.abs(
                                      continentData.landLoss.ratePerYear - 0.8,
                                    ).toFixed(1)}
                                    %
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Integrated Trends Visualization */}
                        <Card className="mb-8">
                          <CardHeader>
                            <CardTitle className="text-lg font-semibold">
                              Environmental Trends
                            </CardTitle>
                            <p className="text-sm text-muted-foreground">
                              Comparative analysis of key environmental
                              indicators
                            </p>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-6">
                              {/* Deforestation Trend Bar */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium">
                                    Forest Loss
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    {continentData.deforestation.ratePerYear.toFixed(
                                      2,
                                    )}
                                    %
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-gradient-to-r from-green-500 to-red-500 h-2 rounded-full trend-bar"
                                    style={{
                                      ["--trend-bar-width" as any]: `${Math.min(continentData.deforestation.ratePerYear * 20, 100)}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>

                              {/* Desertification Trend Bar */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium">
                                    Desertification
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    {continentData.desertification.ratePerYear.toFixed(
                                      2,
                                    )}
                                    %
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-gradient-to-r from-green-500 to-orange-500 h-2 rounded-full trend-bar"
                                    style={{
                                      ["--trend-bar-width" as any]: `${Math.min(continentData.desertification.ratePerYear * 25, 100)}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>

                              {/* Land Loss Trend Bar */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium">
                                    Land Loss
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    {continentData.landLoss.ratePerYear.toFixed(
                                      2,
                                    )}
                                    %
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-gradient-to-r from-green-500 to-purple-500 h-2 rounded-full trend-bar"
                                    style={{
                                      ["--trend-bar-width" as any]: `${Math.min(continentData.landLoss.ratePerYear * 30, 100)}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>

                              {/* Species Threat Level */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium">
                                    Species Threat Level
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    {continentData.endangeredSpecies.count}{" "}
                                    species
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-gradient-to-r from-green-500 to-amber-500 h-2 rounded-full trend-bar"
                                    style={{
                                      ["--trend-bar-width" as any]: `${Math.min(continentData.endangeredSpecies.count / 2, 100)}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Top Threatened Species - Modernized */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg font-semibold">
                              Priority Conservation Species
                            </CardTitle>
                            <p className="text-sm text-muted-foreground">
                              Species requiring immediate conservation attention
                            </p>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {processBarChartData()
                                .slice(0, 6)
                                .map((entry, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50"
                                  >
                                    <div
                                      className={`w-3 h-3 rounded-full ${
                                        idx < 2
                                          ? "bg-red-500"
                                          : idx < 4
                                            ? "bg-amber-500"
                                            : "bg-green-500"
                                      }`}
                                    ></div>
                                    {/* Species Name and Conservation Status */}
                                    <div className="flex-1 text-left">
                                      <div className="font-medium text-sm">
                                        {entry.name}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {idx < 2
                                          ? "Critically Endangered"
                                          : idx < 4
                                            ? "Endangered"
                                            : "Vulnerable"}
                                      </div>
                                    </div>
                                    {/* Population Count with Emphasis */}
                                    <div className="text-right">
                                      <div className="text-sm font-semibold">
                                        {entry.population.toLocaleString()}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        population
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>

                            {/* Conservation Status Legend */}
                            <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-border">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <span className="text-xs text-muted-foreground">
                                  Critically Endangered
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                                <span className="text-xs text-muted-foreground">
                                  Endangered
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                <span className="text-xs text-muted-foreground">
                                  Vulnerable
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Environmental Conditions Report Tables */}
                        <div className="mt-8 space-y-6">
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-semibold text-foreground">
                              Environmental Conditions Report
                            </h3>
                            <Badge variant="outline" className="text-[#40e0d0]">
                              Detailed Analysis
                            </Badge>
                          </div>

                          {/* Air Quality Conditions Table */}
                          <Card>
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                  <Activity className="w-5 h-5 text-blue-500" />
                                  Air Quality Conditions
                                </CardTitle>
                                {continentAirQualityData?.coverage && (
                                  <Badge variant="outline" className="text-xs">
                                    {
                                      continentAirQualityData.coverage
                                        .regionsWithData
                                    }
                                    /
                                    {
                                      continentAirQualityData.coverage
                                        .totalRegions
                                    }{" "}
                                    regions (
                                    {
                                      continentAirQualityData.coverage
                                        .coveragePercentage
                                    }
                                    % coverage)
                                  </Badge>
                                )}
                              </div>
                              {continentAirQualityData?.coverage && (
                                <p className="text-sm text-muted-foreground text-left pt-4">
                                  <i>
                                    Continent-wide air quality statistics
                                    aggregated from
                                    <strong>
                                      {" "}
                                      {
                                        continentAirQualityData.coverage
                                          .regionsWithData
                                      }{" "}
                                    </strong>
                                    monitoring stations. Values show{" "}
                                    <strong>mean</strong> concentrations with
                                    observed ranges in parentheses.
                                  </i>
                                </p>
                              )}
                            </CardHeader>
                            <CardContent>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Air Quality Metric
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Mean Value (Range)
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Overall Status
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Regional Trend
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        <div className="flex items-center gap-2">
                                          WHO Standard
                                          <UITooltip>
                                            <UITooltipTrigger>
                                              <Info className="w-4 h-4 cursor-help" />
                                            </UITooltipTrigger>
                                            <UITooltipContent className="max-w-xs">
                                              <p className="font-medium text-sm">
                                                WHO Air Quality Guidelines
                                                (2021)
                                              </p>
                                              <div className="text-xs mt-1 space-y-1">
                                                <div>
                                                  PM2.5: ≤5 μg/m³ (annual)
                                                </div>
                                                <div>
                                                  PM10: ≤15 μg/m³ (annual)
                                                </div>
                                                <div>
                                                  NO₂: ≤10 μg/m³ (annual)
                                                </div>
                                                <div>
                                                  O₃: ≤60 μg/m³ (peak season)
                                                </div>
                                              </div>
                                              <p className="text-xs mt-2 opacity-75">
                                                Updated standards from WHO's
                                                latest guidelines
                                              </p>
                                            </UITooltipContent>
                                          </UITooltip>
                                        </div>
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="text-left">
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        PM2.5 Concentration
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {getAirQualityValue("pm25")}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge
                                          variant={
                                            getAirQualityStatus("pm25").variant
                                          }
                                        >
                                          {getAirQualityStatus("pm25").status}
                                        </Badge>
                                      </td>
                                      <td
                                        className={`py-3 px-4 ${getAirQualityTrend("pm25").color}`}
                                      >
                                        {getAirQualityTrend("pm25").trend}
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        ≤5 μg/m³
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        PM10 Concentration
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {getAirQualityValue("pm10")}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge
                                          variant={
                                            getAirQualityStatus("pm10").variant
                                          }
                                        >
                                          {getAirQualityStatus("pm10").status}
                                        </Badge>
                                      </td>
                                      <td
                                        className={`py-3 px-4 ${getAirQualityTrend("pm10").color}`}
                                      >
                                        {getAirQualityTrend("pm10").trend}
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        ≤15 μg/m³
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">NO₂ Level</td>
                                      <td className="py-3 px-4 font-medium">
                                        {getAirQualityValue("no2")}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge
                                          variant={
                                            getAirQualityStatus("no2").variant
                                          }
                                        >
                                          {getAirQualityStatus("no2").status}
                                        </Badge>
                                      </td>
                                      <td
                                        className={`py-3 px-4 ${getAirQualityTrend("no2").color}`}
                                      >
                                        {getAirQualityTrend("no2").trend}
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        ≤10 μg/m³
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">Ozone (O₃)</td>
                                      <td className="py-3 px-4 font-medium">
                                        {getAirQualityValue("o3")}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge
                                          variant={
                                            getAirQualityStatus("o3").variant
                                          }
                                        >
                                          {getAirQualityStatus("o3").status}
                                        </Badge>
                                      </td>
                                      <td
                                        className={`py-3 px-4 ${getAirQualityTrend("o3").color}`}
                                      >
                                        {getAirQualityTrend("o3").trend}
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        ≤60 μg/m³
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Climate Conditions Table */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-orange-500" />
                                Climate Conditions
                              </CardTitle>
                              <p className="text-sm text-muted-foreground text-left pt-4">
                                Data aggregated from all available regions
                                across<strong> {selectedContinent}</strong>,
                                providing continent-wide averages for
                                comprehensive climate assessment.
                              </p>
                            </CardHeader>
                            <CardContent>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Climate Factor
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Current Value
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Classification
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        10-Year Trend
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Historical Avg
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="text-left">
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Average Temperature
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {getWeatherValue("temperature")}°C
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge
                                          variant={
                                            getWeatherClassification(
                                              "temperature",
                                            ).variant
                                          }
                                        >
                                          {
                                            getWeatherClassification(
                                              "temperature",
                                            ).status
                                          }
                                        </Badge>
                                      </td>
                                      <td
                                        className={`py-3 px-4 ${getWeatherTrend("temperature").color}`}
                                      >
                                        {getWeatherTrend("temperature").trend}
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        {getWeatherHistoricalAvg("temperature")}
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Annual Precipitation
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {getWeatherValue("precipitation")} mm
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge
                                          variant={
                                            getWeatherClassification(
                                              "precipitation",
                                            ).variant
                                          }
                                        >
                                          {
                                            getWeatherClassification(
                                              "precipitation",
                                            ).status
                                          }
                                        </Badge>
                                      </td>
                                      <td
                                        className={`py-3 px-4 ${getWeatherTrend("precipitation").color}`}
                                      >
                                        {getWeatherTrend("precipitation").trend}
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        {getWeatherHistoricalAvg(
                                          "precipitation",
                                        )}
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Humidity Level
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {getWeatherValue("humidity")}%
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge
                                          variant={
                                            getWeatherClassification("humidity")
                                              .variant
                                          }
                                        >
                                          {
                                            getWeatherClassification("humidity")
                                              .status
                                          }
                                        </Badge>
                                      </td>
                                      <td
                                        className={`py-3 px-4 ${getWeatherTrend("humidity").color}`}
                                      >
                                        {getWeatherTrend("humidity").trend}
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        {getWeatherHistoricalAvg("humidity")}
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">Visibility</td>
                                      <td className="py-3 px-4 font-medium">
                                        {getWeatherValue("visibility")} km
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge
                                          variant={
                                            getWeatherClassification(
                                              "visibility",
                                            ).variant
                                          }
                                        >
                                          {
                                            getWeatherClassification(
                                              "visibility",
                                            ).status
                                          }
                                        </Badge>
                                      </td>
                                      <td
                                        className={`py-3 px-4 ${getWeatherTrend("visibility").color}`}
                                      >
                                        {getWeatherTrend("visibility").trend}
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        {getWeatherHistoricalAvg("visibility")}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Water Quality Conditions Table */}
                          <Card className="bg-red-50/30 border-red-200/40 dark:bg-red-950/10 dark:border-red-900/30">
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                  <Globe className="w-5 h-5 text-cyan-500" />
                                  Water Quality Conditions
                                </CardTitle>
                                <UITooltip>
                                  <UITooltipTrigger asChild>
                                    <div className="flex items-center gap-2 px-2 py-1 bg-red-100 dark:bg-red-900/30 rounded-full text-red-700 dark:text-red-300 text-xs font-medium cursor-help">
                                      <AlertTriangle className="w-3 h-3" />
                                      Mock Data
                                    </div>
                                  </UITooltipTrigger>
                                  <UITooltipContent>
                                    <p className="text-sm">
                                      This table currently displays simulated
                                      data for demonstration purposes.
                                      <br />
                                      Real water quality monitoring data will be
                                      available in future updates.
                                    </p>
                                  </UITooltipContent>
                                </UITooltip>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Parameter
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Current Level
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Quality Rating
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Trend
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Acceptable Range
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">pH Level</td>
                                      <td className="py-3 px-4 font-medium">
                                        {(Math.random() * 2 + 6.5).toFixed(2)}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="default">
                                          Excellent
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-amber-600">
                                        → Stable
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        6.5-8.5
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Dissolved Oxygen
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {(Math.random() * 5 + 5).toFixed(1)}{" "}
                                        mg/L
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="default">Good</Badge>
                                      </td>
                                      <td className="py-3 px-4 text-green-600">
                                        ↑ Improving
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        ≥5 mg/L
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">Turbidity</td>
                                      <td className="py-3 px-4 font-medium">
                                        {(Math.random() * 10 + 2).toFixed(1)}{" "}
                                        NTU
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="secondary">Fair</Badge>
                                      </td>
                                      <td className="py-3 px-4 text-red-600">
                                        ↑ Worsening
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        ≤4 NTU
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Total Coliform
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {Math.floor(Math.random() * 200)}{" "}
                                        CFU/100mL
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="destructive">
                                          Poor
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-red-600">
                                        ↑ Increasing
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        ≤10 CFU/100mL
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Heavy Metals (Pb)
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {(Math.random() * 0.01).toFixed(3)} mg/L
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="default">
                                          Excellent
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-green-600">
                                        ↓ Decreasing
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        ≤0.01 mg/L
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Soil Conditions Table */}
                          <Card className="bg-red-50/30 border-red-200/40 dark:bg-red-950/10 dark:border-red-900/30">
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                  <TreePine className="w-5 h-5 text-green-600" />
                                  Soil Health Conditions
                                </CardTitle>
                                <UITooltip>
                                  <UITooltipTrigger asChild>
                                    <div className="flex items-center gap-2 px-2 py-1 bg-red-100 dark:bg-red-900/30 rounded-full text-red-700 dark:text-red-300 text-xs font-medium cursor-help">
                                      <AlertTriangle className="w-3 h-3" />
                                      Mock Data
                                    </div>
                                  </UITooltipTrigger>
                                  <UITooltipContent>
                                    <p className="text-sm">
                                      This table currently displays simulated
                                      data for demonstration purposes.
                                      <br />
                                      Real soil monitoring data will be
                                      available in future updates.
                                    </p>
                                  </UITooltipContent>
                                </UITooltip>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Indicator
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Measurement
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Health Status
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Change
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Optimal Range
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">Soil pH</td>
                                      <td className="py-3 px-4 font-medium">
                                        {(Math.random() * 2 + 6).toFixed(2)}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="default">Good</Badge>
                                      </td>
                                      <td className="py-3 px-4 text-amber-600">
                                        → Stable
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        6.0-7.5
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Organic Matter
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {(Math.random() * 3 + 2).toFixed(1)}%
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="default">Good</Badge>
                                      </td>
                                      <td className="py-3 px-4 text-green-600">
                                        ↑ Increasing
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        3-6%
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Nitrogen (N)
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {(Math.random() * 50 + 20).toFixed(0)}{" "}
                                        ppm
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="secondary">
                                          Moderate
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-red-600">
                                        ↓ Declining
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        40-60 ppm
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Phosphorus (P)
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {(Math.random() * 30 + 15).toFixed(0)}{" "}
                                        ppm
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="default">
                                          Adequate
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-amber-600">
                                        → Stable
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        25-50 ppm
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Soil Erosion Rate
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {(Math.random() * 10 + 2).toFixed(1)}{" "}
                                        t/ha/year
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="destructive">
                                          High
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-red-600">
                                        ↑ Increasing
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        ≤2 t/ha/year
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Biodiversity Status Table */}
                          <Card className="bg-red-50/30 border-red-200/40 dark:bg-red-950/10 dark:border-red-900/30">
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                  <Users className="w-5 h-5 text-purple-500" />
                                  Biodiversity Status
                                </CardTitle>
                                <UITooltip>
                                  <UITooltipTrigger asChild>
                                    <div className="flex items-center gap-2 px-2 py-1 bg-red-100 dark:bg-red-900/30 rounded-full text-red-700 dark:text-red-300 text-xs font-medium cursor-help">
                                      <AlertTriangle className="w-3 h-3" />
                                      Mock Data
                                    </div>
                                  </UITooltipTrigger>
                                  <UITooltipContent>
                                    <p className="text-sm">
                                      This table currently displays simulated
                                      data for demonstration purposes.
                                      <br />
                                      Real biodiversity monitoring data will be
                                      available in future updates.
                                    </p>
                                  </UITooltipContent>
                                </UITooltip>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Species Category
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Population Count
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Conservation Status
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Population Trend
                                      </th>
                                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                                        Key Threats
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">Mammals</td>
                                      <td className="py-3 px-4 font-medium">
                                        {Math.floor(
                                          Math.random() * 50000 + 25000,
                                        ).toLocaleString()}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="destructive">
                                          Threatened
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-red-600">
                                        ↓ -15% declining
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        Habitat loss, hunting
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">Birds</td>
                                      <td className="py-3 px-4 font-medium">
                                        {Math.floor(
                                          Math.random() * 100000 + 75000,
                                        ).toLocaleString()}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="secondary">
                                          Vulnerable
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-amber-600">
                                        → -3% slight decline
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        Climate change, pollution
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">Fish</td>
                                      <td className="py-3 px-4 font-medium">
                                        {Math.floor(
                                          Math.random() * 200000 + 150000,
                                        ).toLocaleString()}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="destructive">
                                          Critically Low
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-red-600">
                                        ↓ -28% severe decline
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        Overfishing, water pollution
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">Insects</td>
                                      <td className="py-3 px-4 font-medium">
                                        {Math.floor(
                                          Math.random() * 1000000 + 500000,
                                        ).toLocaleString()}
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="default">Stable</Badge>
                                      </td>
                                      <td className="py-3 px-4 text-green-600">
                                        ↑ +2% recovering
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        Pesticides, habitat fragmentation
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                      <td className="py-3 px-4">
                                        Plant Species
                                      </td>
                                      <td className="py-3 px-4 font-medium">
                                        {Math.floor(
                                          Math.random() * 5000 + 2000,
                                        ).toLocaleString()}{" "}
                                        species
                                      </td>
                                      <td className="py-3 px-4">
                                        <Badge variant="secondary">
                                          At Risk
                                        </Badge>
                                      </td>
                                      <td className="py-3 px-4 text-red-600">
                                        ↓ -8% declining
                                      </td>
                                      <td className="py-3 px-4 text-muted-foreground">
                                        Deforestation, invasive species
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-8 bg-white dark:bg-gray-800/50 rounded-lg border border-border/40">
                        <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                          <svg
                            className="w-12 h-12 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">
                          Data Collection in Progress
                        </h3>
                        <p className="text-muted-foreground text-center max-w-md">
                          We're currently sourcing environmental data for{" "}
                          {selectedContinent}. This helps us ensure accurate and
                          comprehensive information for your analysis.
                        </p>
                      </div>
                    )}
                  </section>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-24 h-24 bg-[#40e0d0]/10 rounded-full flex items-center justify-center mb-6">
                      <Globe className="w-12 h-12 text-[#40e0d0]" />
                    </div>
                    <h1 className="text-[3.5rem] font-semibold text-foreground mb-2">
                      No Region Selected for Analysis
                    </h1>
                    <p className="text-muted-foreground mb-6 text-center max-w-md text-[1.25rem]">
                      Get detailed regional environmental analysis and insights
                      for any continent. Select a region/continent from the
                      dropdown above to view comprehensive data.
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* Predictions Tab Content */}
              <TabsContent value="predictions" className="space-y-6">
                {/* Prediction Section */}
                <section
                  id="prediction-section"
                  className="bg-[#f3f4f6]/20 dark:bg-[#272727] backdrop-blur-sm rounded-lg border border-border/40 p-6"
                >
                  <div className="flex items-center gap-2 mb-6 bg-black-transparent/10 p-4 rounded-lg bg-[#f3f4f6] dark:bg-[#1c1c1c]">
                    <span className="text-sm  text-muted-foreground font-medium">
                      Projections are stored for 12 months&nbsp; &nbsp; •&nbsp;
                      &nbsp;
                      <strong>
                        Generates a 5 - 10 year forecast. Credits are deducted
                        only after successful completion.
                      </strong>
                    </span>
                    {/* Action Buttons */}
                    <div className="flex items-center gap-3 ml-auto shrink-0">
                      <div className="relative">
                        {/* Save hint tooltip */}
                        <div
                          className={`absolute -top-11 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 rounded-lg bg-[#40e0d0] text-grey-500 text-xs font-medium shadow-lg pointer-events-none transition-all duration-8000 ${
                            showSaveHint
                              ? "opacity-100 -translate-y-0"
                              : "opacity-0 translate-y-1"
                          }`}
                        >
                          <BookmarkPlus className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
                          Save your projection now!
                          {/* Arrow */}
                          <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#40e0d0]" />
                        </div>
                        <Button
                          size="lg"
                          variant="outline"
                          className="border-[#40e0d0]/40 text-[#40e0d0] hover:bg-[#40e0d0]/10 hover:border-[#40e0d0]"
                          disabled={!prediction || !selectedRegion}
                          onClick={handleSavePrediction}
                        >
                          <BookmarkPlus className="w-4 h-4 mr-2" />
                          Save Projection
                        </Button>
                      </div>
                      <Button
                        size="lg"
                        variant="outline"
                        className="text-foreground hover:bg-[#40e0d0] dark:hover:bg-gray-700 bg-[#40e0d0]/10 hover:border-[#40e0d0]/40"
                        onClick={() => {
                          setShowHistoryPanel(true);
                          setHistoryRefreshKey((k) => k + 1);
                        }}
                      >
                        <History className="w-4 h-4 mr-2" />
                        History
                      </Button>
                    </div>
                  </div>
                  {prediction && showPrediction ? (
                    <div className="space-y-8">
                      {/* Modern Hero Section */}
                      <div className="relative overflow-hidden rounded-2xl bg-[#1c1c1c]/5 dark:bg-[#1c1c1c] border border-[#1c1c1c]/5 backdrop-blur-sm">
                        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
                        <div className="relative p-8">
                          <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#40e0d0] to-[#00bcd4] flex items-center justify-center shadow-lg mb-4">
                                <Brain className="w-8 h-8 text-white" />
                              </div>
                              <div className="text-left">
                                <h1 className="text-2xl font-semibold text-[#1d1d1d] dark:text-white mb-4">
                                  AI Environmental Projections
                                </h1>
                                <p className="text-muted-foreground text-[1rem] text-left">
                                  5- &amp; 10-Year Impact Analysis for{" "}
                                  <span className="font-bold uppercase tracking-wide">
                                    {selectedRegion?.name}
                                  </span>
                                  <span className="text-muted-foreground text-[.75rem] font-bold">
                                    {" "}
                                    &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;&nbsp;Projections
                                    cost 3 credits
                                  </span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge
                                variant="outline"
                                className={`
                            ${
                              hasAccess("aiInsights")
                                ? "bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30 text-green-700 dark:text-green-400"
                                : "bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400"
                            }
                          `}
                              >
                                <Sparkles className="w-3 h-3 mr-1" />
                                {hasAccess("aiInsights")
                                  ? "AI-Powered"
                                  : "AI-Powered (Premium)"}
                              </Badge>
                            </div>
                          </div>

                          {/* Smart Region Selector */}
                          <div className="grid grid-cols-1 lg:grid-cols-3 text-left mt-[3rem] gap-6">
                            <div className="lg:col-span-2">
                              <label className="text-sm font-medium text-muted-foreground mb-2 block text-left">
                                Change Region
                              </label>
                              <Popover
                                open={regionComboOpen}
                                onOpenChange={setRegionComboOpen}
                              >
                                <PopoverTrigger asChild>
                                  <button
                                    disabled={
                                      predictionLoading ||
                                      regions.length === 0 ||
                                      !hasAccess("aiInsights")
                                    }
                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md border bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-sm transition-colors ${
                                      hasAccess("aiInsights")
                                        ? "border-[#40e0d0]/30 hover:border-[#40e0d0]/60"
                                        : "border-amber-500/30 opacity-60 cursor-not-allowed"
                                    }`}
                                  >
                                    <span
                                      className={
                                        selectedRegion
                                          ? "text-foreground"
                                          : "text-muted-foreground"
                                      }
                                    >
                                      {!hasAccess("aiInsights")
                                        ? "Premium feature — Upgrade to access"
                                        : regions.length === 0
                                          ? "Loading regions..."
                                          : selectedRegion
                                            ? selectedRegion.name
                                            : "Search for a region..."}
                                    </span>
                                    <ChevronsUpDown className="w-4 h-4 text-muted-foreground shrink-0 ml-2" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-[var(--radix-popover-trigger-width)] p-0"
                                  align="start"
                                >
                                  <Command>
                                    <CommandInput
                                      placeholder="Type to search regions..."
                                      className="h-9"
                                    />
                                    <CommandList>
                                      <CommandEmpty>
                                        No region found.
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {regions.map((region) => (
                                          <CommandItem
                                            key={region.id}
                                            value={region.name}
                                            onSelect={(val) => {
                                              const regionObj = regions.find(
                                                (r) =>
                                                  r.name.toLowerCase() ===
                                                  val.toLowerCase(),
                                              );
                                              setSelectedRegion(
                                                regionObj || null,
                                              );
                                              setRegionComboOpen(false);
                                            }}
                                            className="flex items-center gap-3"
                                          >
                                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shrink-0">
                                              <MapIcon className="w-3.5 h-3.5 text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <p className="font-medium truncate">
                                                {region.name}
                                              </p>
                                              <p className="text-xs text-muted-foreground truncate">
                                                {region.country}
                                              </p>
                                            </div>
                                            {selectedRegion?.id ===
                                              region.id && (
                                              <Check className="w-4 h-4 text-[#40e0d0] shrink-0" />
                                            )}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>

                            {/* Timeframe Selector - view toggle only, does not affect credit cost */}
                            <div className="lg:col-span-1">
                              <div className="flex items-center gap-1.5 mb-2">
                                <Label className="text-sm font-medium text-muted-foreground">
                                  View timeframe
                                </Label>
                                <UITooltip>
                                  <UITooltipTrigger asChild>
                                    <Info className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                                  </UITooltipTrigger>
                                  <UITooltipContent
                                    side="top"
                                    className="max-w-[220px] text-xs"
                                  >
                                    Every projection includes both 5-year and
                                    10-year forecasts. This toggle only changes
                                    which horizon is displayed — it does not
                                    affect the 3-credit cost.
                                  </UITooltipContent>
                                </UITooltip>
                              </div>
                              <RadioGroup
                                value={predictionTimeframe}
                                onValueChange={(value) =>
                                  setPredictionTimeframe(value as "5y" | "10y")
                                }
                                className="flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 p-1 rounded-lg bg-gray-500/5"
                              >
                                <Label
                                  htmlFor="tf-5y"
                                  className={`cursor-pointer px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${predictionTimeframe === "5y" ? "bg-[#40e0d0]/10 text-[#40e0d0]" : "text-muted-foreground"}`}
                                >
                                  <RadioGroupItem
                                    value="5y"
                                    id="tf-5y"
                                    className="sr-only"
                                  />
                                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                                    <Calendar className="w-3 h-3 text-white" />
                                  </div>
                                  <span>5 Years</span>
                                </Label>

                                <Label
                                  htmlFor="tf-10y"
                                  className={`cursor-pointer px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${predictionTimeframe === "10y" ? "bg-[#40e0d0]/10 text-[#40e0d0]" : "text-muted-foreground"}`}
                                >
                                  <RadioGroupItem
                                    value="10y"
                                    id="tf-10y"
                                    className="sr-only"
                                  />
                                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                                    <Zap className="w-3 h-3 text-white" />
                                  </div>
                                  <span>10 Years</span>
                                </Label>
                              </RadioGroup>
                              <p className="text-[10px] text-muted-foreground/50 mt-1 pl-1">
                                Both horizons are always computed
                              </p>
                            </div>
                            {/* Action Buttons Row */}
                            <div className="lg:col-span-1 flex items-center">
                              <Button
                                className="w-full bg-[#40e0d0] hover:bg-[#40e0d0]/80 text-gray-800 font-semibold text-[1rem] dark:bg-[#40e0d0]/80 dark:hover:bg-[#40e0d0]/60 dark:text-black"
                                size="sm"
                                disabled={
                                  predictionLoading ||
                                  !hasAccess("aiInsights") ||
                                  !selectedRegion?.name
                                }
                                onClick={() => {
                                  if (selectedRegion?.name) {
                                    handleGeneratePrediction(
                                      selectedRegion.name,
                                    );
                                  }
                                }}
                              >
                                {predictionLoading
                                  ? "Running..."
                                  : "Run Prediction"}
                              </Button>
                            </div>

                            {/* Loading indicator */}
                            {predictionLoading && (
                              <div className="flex items-center gap-3 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl backdrop-blur-sm">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#40e0d0]"></div>
                                <div>
                                  <p className="font-medium text-[#40e0d0]">
                                    Analyzing Data
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Generating prediction...
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {prediction?.predictions ? (
                        <>
                          {/* Enhanced Metric Cards */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Deforestation Card */}
                            <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left">
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-red-500/10 dark:bg-red-500/20 flex items-center justify-center">
                                      <TreePine className="w-6 h-6 text-red-500" />
                                    </div>
                                    <div>
                                      <CardTitle className="text-lg font-semibold">
                                        Deforestation
                                      </CardTitle>
                                      <p className="text-sm text-muted-foreground">
                                        Rate per year
                                      </p>
                                    </div>
                                  </div>
                                  <Badge
                                    className={`text-[0.55rem] border-transparent ${
                                      getPredictionValue("deforestation") > 2
                                        ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                        : getPredictionValue("deforestation") >
                                            1
                                          ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                    }`}
                                  >
                                    {getPredictionValue("deforestation") > 2
                                      ? "High"
                                      : getPredictionValue("deforestation") > 1
                                        ? "Medium"
                                        : "Low"}
                                  </Badge>
                                </div>
                              </CardHeader>

                              <CardContent>
                                <div className="space-y-4">
                                  <div className="flex items-baseline gap-2">
                                    <span
                                      className={`text-4xl font-bold ${
                                        getPredictionValue("deforestation") > 2
                                          ? "text-red-500"
                                          : getPredictionValue(
                                                "deforestation",
                                              ) > 1
                                            ? "text-yellow-500"
                                            : "text-green-500"
                                      }`}
                                    >
                                      {getPredictionValue(
                                        "deforestation",
                                      ).toFixed(1)}
                                    </span>
                                    <span className="text-lg text-muted-foreground">
                                      %
                                    </span>
                                  </div>

                                  {/* Confidence Progress Bar */}
                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">
                                        Confidence Level
                                      </span>
                                      <span className="font-medium">
                                        {(
                                          (prediction.predictions.confidence
                                            ?.deforestation || 0) * 100
                                        ).toFixed(0)}
                                        %
                                      </span>
                                    </div>
                                    <Progress
                                      value={
                                        (prediction.predictions.confidence
                                          ?.deforestation || 0) * 100
                                      }
                                      className={`h-2 ${
                                        (prediction.predictions.confidence
                                          ?.deforestation || 0) *
                                          100 <=
                                        50
                                          ? "[&>div]:bg-red-500"
                                          : (prediction.predictions.confidence
                                                ?.deforestation || 0) *
                                                100 <=
                                              75
                                            ? "[&>div]:bg-orange-500"
                                            : "[&>div]:bg-teal-500"
                                      }`}
                                    />
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Desertification Card */}
                            <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left">
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-orange-500/10 dark:bg-orange-500/20 flex items-center justify-center">
                                      <Zap className="w-6 h-6 text-orange-500" />
                                    </div>
                                    <div>
                                      <CardTitle className="text-lg font-semibold">
                                        Desertification
                                      </CardTitle>
                                      <p className="text-sm text-muted-foreground">
                                        Area affected
                                      </p>
                                    </div>
                                  </div>
                                  <Badge
                                    className={`text-[0.55rem] border-transparent ${
                                      getPredictionValue("desertification") >
                                      500
                                        ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                        : getPredictionValue(
                                              "desertification",
                                            ) > 200
                                          ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                    }`}
                                  >
                                    {getPredictionValue("desertification") > 500
                                      ? "High"
                                      : getPredictionValue("desertification") >
                                          200
                                        ? "Medium"
                                        : "Low"}
                                  </Badge>
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-4">
                                  <div className="flex items-baseline gap-2">
                                    <span
                                      className={`text-4xl font-bold ${
                                        getPredictionValue("desertification") >
                                        500
                                          ? "text-red-500"
                                          : getPredictionValue(
                                                "desertification",
                                              ) > 200
                                            ? "text-yellow-500"
                                            : "text-green-500"
                                      }`}
                                    >
                                      {getPredictionValue(
                                        "desertification",
                                      ).toFixed(1)}
                                    </span>
                                    <span className="text-lg text-muted-foreground">
                                      km²
                                    </span>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">
                                        Confidence Level
                                      </span>
                                      <span className="font-medium">
                                        {(
                                          (prediction.predictions.confidence
                                            ?.desertification || 0) * 100
                                        ).toFixed(0)}
                                        %
                                      </span>
                                    </div>
                                    {/* Confidence Progress Bar */}
                                    <Progress
                                      value={
                                        (prediction.predictions.confidence
                                          ?.desertification || 0) * 100
                                      }
                                      className={`h-2 ${
                                        (prediction.predictions.confidence
                                          ?.desertification || 0) *
                                          100 <=
                                        50
                                          ? "[&>div]:bg-red-500"
                                          : (prediction.predictions.confidence
                                                ?.desertification || 0) *
                                                100 <=
                                              75
                                            ? "[&>div]:bg-orange-500"
                                            : "[&>div]:bg-teal-500"
                                      }`}
                                    />
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Species Impact Card */}
                            <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left">
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 dark:bg-purple-500/20 flex items-center justify-center">
                                      <Target className="w-6 h-6 text-purple-500" />
                                    </div>
                                    <div>
                                      <CardTitle className="text-lg font-semibold">
                                        Species Impact
                                      </CardTitle>
                                      <p className="text-sm text-muted-foreground">
                                        Biodiversity risk
                                      </p>
                                    </div>
                                  </div>
                                  <Badge
                                    className={`text-[0.55rem] border-transparent ${
                                      getPredictionValue("species") > 15
                                        ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                        : getPredictionValue("species") > 8
                                          ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                    }`}
                                  >
                                    {getPredictionValue("species") > 15
                                      ? "High"
                                      : getPredictionValue("species") > 8
                                        ? "Medium"
                                        : "Low"}
                                  </Badge>
                                </div>
                              </CardHeader>

                              <CardContent>
                                <div className="space-y-4">
                                  <div className="flex items-baseline gap-2">
                                    <span
                                      className={`text-4xl font-bold ${
                                        getPredictionValue("species") > 15
                                          ? "text-red-500"
                                          : getPredictionValue("species") > 8
                                            ? "text-yellow-500"
                                            : "text-green-500"
                                      }`}
                                    >
                                      {getPredictionValue("species").toFixed(1)}
                                    </span>
                                    <span className="text-lg text-muted-foreground">
                                      %
                                    </span>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">
                                        Confidence Level
                                      </span>
                                      <span className="font-medium">
                                        {(
                                          (prediction.predictions.confidence
                                            ?.species || 0) * 100
                                        ).toFixed(0)}
                                        %
                                      </span>
                                    </div>
                                    <Progress
                                      value={
                                        (prediction.predictions.confidence
                                          ?.species || 0) * 100
                                      }
                                      className={`h-2 ${
                                        (prediction.predictions.confidence
                                          ?.species || 0) *
                                          100 <=
                                        50
                                          ? "[&>div]:bg-red-500"
                                          : (prediction.predictions.confidence
                                                ?.species || 0) *
                                                100 <=
                                              75
                                            ? "[&>div]:bg-orange-500"
                                            : "[&>div]:bg-teal-500"
                                      }`}
                                    />
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Land Loss Card */}
                            <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left">
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center">
                                      <Globe className="w-6 h-6 text-blue-500" />
                                    </div>
                                    <div>
                                      <CardTitle className="text-lg font-semibold">
                                        Land Loss
                                      </CardTitle>
                                      <p className="text-sm text-muted-foreground">
                                        Area at risk
                                      </p>
                                    </div>
                                  </div>
                                  <Badge
                                    className={`text-[0.55rem] border-transparent ${
                                      getPredictionValue("landLoss") > 300
                                        ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                        : getPredictionValue("landLoss") > 100
                                          ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                    }`}
                                  >
                                    {getPredictionValue("landLoss") > 300
                                      ? "High"
                                      : getPredictionValue("landLoss") > 100
                                        ? "Medium"
                                        : "Low"}
                                  </Badge>
                                </div>
                              </CardHeader>

                              <CardContent>
                                <div className="space-y-4">
                                  <div className="flex items-baseline gap-2">
                                    <span
                                      className={`text-4xl font-bold ${
                                        getPredictionValue("landLoss") > 300
                                          ? "text-red-500"
                                          : getPredictionValue("landLoss") > 100
                                            ? "text-yellow-500"
                                            : "text-green-500"
                                      }`}
                                    >
                                      {getPredictionValue("landLoss").toFixed(
                                        1,
                                      )}
                                    </span>
                                    <span className="text-lg text-muted-foreground">
                                      km²
                                    </span>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">
                                        Confidence Level
                                      </span>
                                      <span className="font-medium">
                                        {(
                                          (prediction.predictions.confidence
                                            ?.land_loss || 0) * 100
                                        ).toFixed(0)}
                                        %
                                      </span>
                                    </div>
                                    <Progress
                                      value={
                                        (prediction.predictions.confidence
                                          ?.land_loss || 0) * 100
                                      }
                                      className={`h-2 ${
                                        (prediction.predictions.confidence
                                          ?.land_loss || 0) *
                                          100 <=
                                        50
                                          ? "[&>div]:bg-red-500"
                                          : (prediction.predictions.confidence
                                                ?.land_loss || 0) *
                                                100 <=
                                              75
                                            ? "[&>div]:bg-orange-500"
                                            : "[&>div]:bg-teal-500"
                                      }`}
                                    />
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>

                          {/* Prediction visualizations (trends, baseline, drivers, confidence) */}
                          {prediction && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 mb-6">
                              <div className="lg:col-span-2">
                                <PredictionTrends
                                  prediction={prediction}
                                  envData={currentEnvData}
                                  timeframe={predictionTimeframe}
                                />
                              </div>

                              <div className="grid grid-cols-2 lg:col-span-2 gap-6 space-y-4">
                                {/* Prediction Drivers */}
                                <PredictionDrivers
                                  prediction={prediction}
                                  envData={currentEnvData}
                                />
                                {/* Baseline Comparison */}
                                <PredictionComparison
                                  prediction={prediction}
                                  envData={currentEnvData}
                                />
                              </div>
                              <div className="lg:col-span-3 mt-4">
                                {/* Enhanced Summary with Action Items */}
                                <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-900/10">
                                  <CardHeader>
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 rounded-xl bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center">
                                        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500" />
                                      </div>
                                      <CardTitle className="text-amber-800 dark:text-amber-200">
                                        Impact Summary & Recommendations
                                      </CardTitle>
                                    </div>
                                  </CardHeader>
                                  <CardContent>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                      <div>
                                        <h4 className="font-medium mb-3 text-left">
                                          Projected Impact Assessment
                                        </h4>
                                        <p className="text-sm text-muted-foreground leading-relaxed text-left">
                                          Based on current environmental data,{" "}
                                          <strong>
                                            {selectedRegion?.name}
                                          </strong>{" "}
                                          is projected to experience{" "}
                                          <span
                                            className={`font-medium ${
                                              getPredictionValue(
                                                "deforestation",
                                              ) > 2
                                                ? "text-red-600 dark:text-red-400"
                                                : getPredictionValue(
                                                      "deforestation",
                                                    ) > 1
                                                  ? "text-yellow-600 dark:text-yellow-400"
                                                  : "text-green-600 dark:text-green-400"
                                            }`}
                                          >
                                            {getPredictionValue(
                                              "deforestation",
                                            ) > 2
                                              ? "significant"
                                              : getPredictionValue(
                                                    "deforestation",
                                                  ) > 1
                                                ? "moderate"
                                                : "low"}
                                          </span>{" "}
                                          deforestation,{" "}
                                          <span
                                            className={`font-medium ${
                                              getPredictionValue(
                                                "desertification",
                                              ) > 500
                                                ? "text-red-600 dark:text-red-400"
                                                : getPredictionValue(
                                                      "desertification",
                                                    ) > 200
                                                  ? "text-yellow-600 dark:text-yellow-400"
                                                  : "text-green-600 dark:text-green-400"
                                            }`}
                                          >
                                            {getPredictionValue(
                                              "desertification",
                                            ) > 500
                                              ? "severe"
                                              : getPredictionValue(
                                                    "desertification",
                                                  ) > 200
                                                ? "moderate"
                                                : "low"}
                                          </span>{" "}
                                          desertification, and{" "}
                                          <span
                                            className={`font-medium ${
                                              getPredictionValue("species") > 15
                                                ? "text-red-600 dark:text-red-400"
                                                : getPredictionValue(
                                                      "species",
                                                    ) > 8
                                                  ? "text-yellow-600 dark:text-yellow-400"
                                                  : "text-green-600 dark:text-green-400"
                                            }`}
                                          >
                                            {getPredictionValue("species") > 15
                                              ? "critical"
                                              : getPredictionValue("species") >
                                                  8
                                                ? "significant"
                                                : "low"}
                                          </span>{" "}
                                          impact on biodiversity over the next{" "}
                                          {predictionTimeframe === "10y"
                                            ? "10"
                                            : "5"}{" "}
                                          years.
                                        </p>
                                      </div>
                                      <div className="space-y-3 text-left">
                                        <h4 className="font-medium">
                                          Quick Actions
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-xs"
                                          >
                                            <Download className="w-3 h-3 mr-1" />
                                            Export Report
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-xs"
                                          >
                                            <Target className="w-3 h-3 mr-1" />
                                            View Interventions
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </div>
                            </div>
                          )}

                          {/* Prediction Metadata */}
                          <div className="flex justify-between items-center text-sm text-muted-foreground">
                            <p>
                              Projection generated on:{" "}
                              {prediction.timestamp
                                ? new Date(
                                    prediction.timestamp,
                                  ).toLocaleString()
                                : "Unknown"}
                            </p>
                            <Badge variant="outline">Model Version 2.1</Badge>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12">
                          <div className="w-24 h-24 bg-[#40e0d0]/10 rounded-full flex items-center justify-center mb-6">
                            <Brain className="w-12 h-12 text-[#40e0d0]" />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            No Prediction Data Available
                          </h3>
                          <p className="text-muted-foreground mb-6 text-center max-w-md">
                            Unable to load prediction results. Please try
                            generating a new prediction.
                          </p>
                          <Button
                            onClick={() => (window.location.href = "/")}
                            className="bg-[#40e0d0] hover:bg-[#40e0d0]/80 text-white"
                          >
                            Go to Map
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="w-24 h-24 bg-[#40e0d0]/10 rounded-full flex items-center justify-center mb-6">
                        <Brain className="w-12 h-12 text-[#40e0d0]" />
                      </div>
                      <h1 className="text-[3.5rem] font-semibold text-foreground mb-2">
                        No Predictions Available
                      </h1>
                      <p className="text-muted-foreground mb-6 text-center max-w-md text-[1.25rem]">
                        Generate environmental impact predictions by selecting a
                        region from the map and clicking
                        <UITooltip>
                          <UITooltipTrigger asChild>
                            <span className="underline cursor-help">
                              EcoPredict button
                            </span>
                          </UITooltipTrigger>
                          <UITooltipContent>
                            <div className="max-w-xs">
                              <div className="font-medium">
                                This will use 3 credits
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                This action will generate a new output and use 3
                                credits. Credits are deducted only after
                                successful completion.
                              </div>
                            </div>
                          </UITooltipContent>
                        </UITooltip>
                        .
                      </p>
                      <Button
                        onClick={() => (window.location.href = "/")}
                        className="bg-[#40e0d0] hover:bg-[#40e0d0]/80 text-white"
                      >
                        Go to Map
                      </Button>
                    </div>
                  )}
                </section>
              </TabsContent>

              {/* Collaboration Tab Content */}
              <TabsContent value="collaboration" className="space-y-6">
                <section
                  id="collaboration-section"
                  className="bg-[#f3f4f6]/20 dark:bg-[#272727] backdrop-blur-sm rounded-lg border border-border/40 p-6"
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className="text-left">
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Project Collaboration
                      </h2>
                      <p className="text-muted-foreground text-[.75rem]">
                        Inviting 10 guests will cost 10 credits •
                        <span className="font-bold">
                          {" "}
                          No credits are used until invites are accepted
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="text-[#40e0d0] capitalize"
                      >
                        {subscriptionTier} Plan
                      </Badge>
                    </div>
                  </div>

                  <GuestManagementPanel projectId={selectedRegion?.id} />
                </section>
              </TabsContent>
            </Tabs>
          </main>
          <Footer />
        </div>
        <CarbonTrackerIntroModal
          open={showCarbonModal}
          onClose={() => setShowCarbonModal(false)}
          onGetStarted={() => {
            setShowCarbonModal(false);
            setShowDailyLogModal(true);
          }}
        />
        <CarbonDashboard
          open={showCarbonDashboard}
          onClose={() => setShowCarbonDashboard(false)}
        />

        {/* Telegram Integration Modal */}
        {showTelegramModal && user && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="dark:bg-[#1c1c1c] bg-white rounded-2xl p-8 shadow-2xl w-full max-w-[67.2rem] max-h-[90vh] overflow-y-auto relative">
              <button
                className="absolute top-4 right-4 text-black dark:text-white text-xl hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center"
                onClick={() => setShowTelegramModal(false)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-[#40e0d0] text-center">
                  Telegram Carbon Tracker
                </h2>
                <p className="text-muted-foreground text-center mt-2">
                  Track your carbon footprint via Telegram messages
                </p>
              </div>
              {hasAccess("whatsappTracking") ? (
                <TelegramIntegration userId={user.id} />
              ) : (
                <UpgradePrompt
                  title="Telegram Integration Premium Feature"
                  description="Connect your Telegram account to track your carbon footprint by messaging the Myhu bot. Get instant feedback on your environmental impact."
                  featureName="Telegram Carbon Tracking"
                  className="max-w-lg mx-auto"
                />
              )}
            </div>
          </div>
        )}

        {/* Daily Log Modal */}
        {showDailyLogModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="dark:bg-[#1c1c1c] bg-white rounded-2xl p-8 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
              <button
                className="absolute top-4 right-4 text-black dark:text-white text-xl hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center"
                onClick={() => setShowDailyLogModal(false)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-[#40e0d0] text-center">
                  Carbon Footprint Log
                </h2>
                <p className="text-muted-foreground text-center mt-2">
                  Track your daily carbon emissions
                </p>
              </div>
              <DailyLog
                onClose={() => setShowDailyLogModal(false)}
                onDataSaved={triggerRefresh}
              />
            </div>
          </div>
        )}
        {/* Prediction History Slide-out Panel */}
        <div
          className={`fixed inset-y-0 right-0 z-[60] flex flex-col w-full max-w-xl bg-white dark:bg-[#1c1c1c] shadow-2xl border-l border-border transition-transform duration-300 ease-in-out ${
            showHistoryPanel ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Panel Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-foreground text-left">
                Projection History
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Load any saved result without spending credits
              </p>
            </div>
            <button
              onClick={() => setShowHistoryPanel(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close history panel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Panel Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
            <PredictionHistory
              refreshKey={historyRefreshKey}
              tier={subscriptionTier}
              onLoad={(saved) => {
                handleLoadFromHistory(saved);
                setShowHistoryPanel(false);
              }}
            />
          </div>
        </div>

        {/* Backdrop */}
        {showHistoryPanel && (
          <div
            className="fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm"
            onClick={() => setShowHistoryPanel(false)}
          />
        )}
      </div>
    </>
  );
};

export type SubscriptionTier = "PAYG" | "Professional";

export default Dashboard;
